import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiPlus,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from 'react-icons/fi';
import { FaDna } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import InlineLoader from './InlineLoader';
import { apiUrl, fileUrl } from '../utils/api';
import {
  buildBiologiaMolecularDetailPath,
  normalizeAssayId,
} from '../utils/biologiaMolecular';
import { buildSafeUploadFileName } from '../utils/fileNames';

const BM_DEBUG_PREFIX = '[BM DEBUG]';

const summarizeAssayForDebug = (assay = {}) => ({
  activo: assay.activo,
  casos: Array.isArray(assay.casos) ? assay.casos : [],
  ensayo_numero: assay.ensayo_numero || '',
  fecha_ensayo: assay.fecha_ensayo || '',
  muestras: Array.isArray(assay.muestras) ? assay.muestras : [],
  numero_muestras: assay.numero_muestras ?? '',
  patogeno_blanco: assay.patogeno_blanco || '',
  patogenos_blanco: Array.isArray(assay.patogenos_blanco) ? assay.patogenos_blanco : [],
  protocolo_general: assay.protocolo_general || '',
  tipo_ensayo: assay.tipo_ensayo || '',
});

const summarizeFormStateForDebug = (formData = {}, selectedFile = null) => ({
  casos: Array.isArray(formData.casos) ? formData.casos : [],
  corrida_muestras: Array.isArray(formData.corrida_muestras) ? formData.corrida_muestras.length : 0,
  ensayo_numero: formData.ensayo_numero || '',
  fecha_ensayo: formData.fecha_ensayo || '',
  file_name: selectedFile?.name || '',
  muestras: Array.isArray(formData.muestras) ? formData.muestras : [],
  numero_muestras: formData.numero_muestras ?? '',
  patogeno_blanco: formData.patogeno_blanco || '',
  patogenos_blanco: Array.isArray(formData.patogenos_blanco) ? formData.patogenos_blanco : [],
  protocolo_general: formData.protocolo_general || '',
  tipo_ensayo: formData.tipo_ensayo || '',
});

const summarizePayloadForDebug = (payload) => ({
  archivo_adjunto: payload.get('archivo_adjunto')?.name || '',
  casos: payload.get('casos'),
  ensayo_numero: payload.get('ensayo_numero'),
  fecha_ensayo: payload.get('fecha_ensayo'),
  muestras: payload.get('muestras'),
  numero_muestras: payload.get('numero_muestras'),
  patogeno_blanco: payload.get('patogeno_blanco'),
  patogenos_blanco: payload.get('patogenos_blanco'),
  protocolo_general: payload.get('protocolo_general'),
  tipo_ensayo: payload.get('tipo_ensayo'),
});

const summarizeAxiosErrorForDebug = (error) => ({
  data: error?.response?.data,
  message: error?.message,
  method: error?.config?.method,
  status: error?.response?.status,
  url: error?.config?.url,
});

const ASSAY_STEPS = [
  {
    id: 1,
    title: 'Datos del ensayo',
    description: 'Caso, muestra, responsable y blancos diagnósticos.',
  },
  {
    id: 2,
    title: 'Reactivos y mezcla',
    description: 'Primers, mix base y controles de la corrida.',
  },
  {
    id: 3,
    title: 'Tubos y termociclador',
    description: 'Pozos, resultados Ct y programa de amplificación.',
  },
  {
    id: 4,
    title: 'Cierre y validación',
    description: 'Observaciones finales, conformidad y adjuntos.',
  },
];

const CLOSURE_STEP_ID = 4;

const TYPE_OPTIONS = ['PCR', 'RT-PCR', 'qPCR', 'RT-qPCR'];
const CONTROL_NAMES = [
  'Control positivo',
  'Control negativo / NTC',
  'Control extracción',
  'Control RT / sin RT',
];
const RESULT_OPTIONS = [
  { value: 'CONFORME', label: 'Sí, conforme' },
  { value: 'NO_CONFORME', label: 'No conforme' },
  { value: 'REPETIR', label: 'Requiere repetición' },
];
const MATRIX_TISSUE_OTHER_VALUE = 'OTRO';
const MATRIX_TISSUE_OPTIONS = [
  'Hisopado nasal',
  'Hisopado orofaringeo',
  'Hisopado cloacal',
  'Hisopado rectal',
  'Agua',
  'Sangre entera',
  'Suero',
  'Plasma',
  'Frotis',
  'Capa flogistica',
  'Leche',
  'Heces',
  'Orina',
  'Contenido ruminal',
  'Contenido intestinal',
  'Exudado',
  'Trasudado',
  'Tejido',
  'Corazon',
  'Pulmones',
  'Pancreas',
  'Higado',
  'Rinon',
  'Bazo',
  'Cerebelo',
  'Cerebro',
  'Medula espinal',
  'Testiculo',
  'Ovario',
  'Utero',
  'Musculo',
  'Hueso',
  'Piel',
  'Glandula mamaria',
  'Glandula tiroidea',
  'Glandula adrenal',
  'Hoja',
  'Raiz',
  'Fruto',
  'Semilla',
  'Tallo',
  'Cultivo / aislamiento',
  'ADN / ARN extraido',
];
const NUCLEASE_FREE_WATER_LABEL = 'Agua libre de nucleasas';
const FIXED_MIX_COMPONENTS = [
  { key: 'primer_fw', reactivo: 'Primer FW' },
  { key: 'primer_rv', reactivo: 'Primer RV' },
  { key: 'sonda_probe', reactivo: 'Sonda / probe' },
  { key: 'agua_libre_nucleasas', reactivo: NUCLEASE_FREE_WATER_LABEL },
];
const FIXED_MIX_COMPONENT_KEYS = new Set(FIXED_MIX_COMPONENTS.map((component) => component.key));
const FIXED_MIX_COMPONENT_BY_KEY = FIXED_MIX_COMPONENTS.reduce((accumulator, component) => {
  accumulator[component.key] = component;
  return accumulator;
}, {});
const DEFAULT_MIX_LABELS = [
  'Primer FW',
  'Primer RV',
  'Sonda / probe',
  'Buffer / Master Mix',
  'dNTPs',
  'MgCl2 / aditivo',
  'Taq / enzima PCR',
  'RT / enzima retrotranscripción',
  'Muestra ADN/ARN/ADNc',
  NUCLEASE_FREE_WATER_LABEL,
];
const DEFAULT_MIX_CONFIG = {
  volumen_reaccion_objetivo: '25',
  factor_sobrepreparacion: '1.1',
  reacciones_extra: '0',
};
const PCR_PROGRAM_STAGES = [
  'Retrotranscripcion (si aplica)',
  'Desnaturalizacion inicial',
  'Desnaturalizacion',
  'Alineamiento / annealing',
  'Extension',
  'Extension final / hold',
];
const QPCR_PROGRAM_STAGES = [
  'Retrotranscripcion (si aplica)',
  'Activacion / desnaturalizacion inicial',
  'Desnaturalizacion',
  'Alineamiento / extension',
  'Lectura fluorescencia',
  'Hold final',
];
const ALL_PLATE_ROW_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const MAX_PLATE_COLUMN_COUNT = 60;
const MAX_PLATE_COUNT = 2;
const DEFAULT_PLATE_CONFIG = {
  plate_rows: '2',
  plate_columns: '2',
  plate_count: '1',
};

const getTodayInputValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createRowId = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const createPrimerRow = (overrides = {}) => ({
  id: createRowId('primer'),
  reactivo: '',
  identificacion_lote: '',
  concentracion_stock: '',
  concentracion_uso: '',
  ...overrides,
});

const createMixRow = (overrides = {}) => ({
  id: createRowId('mix'),
  clave_componente: '',
  reactivo: '',
  identificacion_lote: '',
  concentracion_inicial: '',
  concentracion_final: '',
  volumen_por_tubo: '',
  cantidad_tubos: '',
  numero_tubos_factor: '',
  volumen_total: '',
  ...overrides,
});

const createCorridaRow = (overrides = {}) => ({
  id: createRowId('corrida'),
  pozo_tubo: '',
  identificacion_muestra: '',
  identificacion_muestra_editada: false,
  muestra_asociada: '',
  fecha_extraccion: '',
  tipo_material: '',
  tipo_registro: 'MUESTRA',
  control_nombre: '',
  ct_resultado: '',
  interpretacion: '',
  ...overrides,
});

const createProgramRow = (overrides = {}) => ({
  id: createRowId('programa'),
  etapa: '',
  temperatura: '',
  tiempo: '',
  ciclos: '',
  observaciones: '',
  ...overrides,
});

const createControls = (savedControls = []) =>
  CONTROL_NAMES.map((name) => {
    const matched = savedControls.find((control) => control?.nombre === name);
    return {
      id: name,
      nombre: name,
      estado: matched?.estado || '',
      identificacion: matched?.identificacion || '',
      observaciones: matched?.observaciones || '',
    };
  });

const createDefaultMixConfig = (overrides = {}) => ({
  ...DEFAULT_MIX_CONFIG,
  ...overrides,
});

const createDefaultPlateConfig = (overrides = {}) => ({
  ...DEFAULT_PLATE_CONFIG,
  ...overrides,
});

const parseNumericValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalizedValue = String(value).replace(',', '.');
  const match = normalizedValue.match(/-?\d+(\.\d+)?/);
  if (!match) {
    return null;
  }

  const parsedValue = Number(match[0]);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatDecimalValue = (value, maximumFractionDigits = 2) => {
  if (!Number.isFinite(value)) {
    return '';
  }

  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value);
};

const buildControlRunLabel = (control = {}) => {
  const customLabel = String(control?.identificacion || '').trim();
  return customLabel || control?.nombre || 'Control';
};

const normalizeReactivoLabel = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const normalizeMatrixTissueLabel = (value = '') =>
  normalizeReactivoLabel(String(value || '').replace(/_/g, ' '));

const getCanonicalMatrixTissueOption = (value = '') => {
  const normalizedValue = normalizeMatrixTissueLabel(value);
  if (!normalizedValue) {
    return '';
  }

  return (
    MATRIX_TISSUE_OPTIONS.find((option) => normalizeMatrixTissueLabel(option) === normalizedValue) || ''
  );
};

const getMatrixTissueOptionValue = (value = '') => {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) {
    return '';
  }

  if (/^otro(\s*:)?$/i.test(trimmedValue)) {
    return MATRIX_TISSUE_OTHER_VALUE;
  }

  return getCanonicalMatrixTissueOption(trimmedValue) || MATRIX_TISSUE_OTHER_VALUE;
};

const getMatrixTissueOtherDetail = (value = '') => {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) {
    return '';
  }

  if (getCanonicalMatrixTissueOption(trimmedValue)) {
    return '';
  }

  const cleanedValue = trimmedValue.replace(/^otro\s*:\s*/i, '').trim();
  return /^otro$/i.test(cleanedValue) ? '' : cleanedValue;
};

const FIXED_MIX_COMPONENT_KEY_BY_LABEL = FIXED_MIX_COMPONENTS.reduce((accumulator, component) => {
  accumulator[normalizeReactivoLabel(component.reactivo)] = component.key;
  return accumulator;
}, {});

const getFixedMixComponentKey = (row = {}) => {
  const storedKey = String(row?.clave_componente || '').trim().toLowerCase();
  if (FIXED_MIX_COMPONENT_KEYS.has(storedKey)) {
    return storedKey;
  }

  return FIXED_MIX_COMPONENT_KEY_BY_LABEL[normalizeReactivoLabel(row?.reactivo)] || '';
};

const applyFixedMixComponentPreset = (row = {}) => {
  const key = getFixedMixComponentKey(row);
  const preset = FIXED_MIX_COMPONENT_BY_KEY[key];
  if (!preset) {
    return row;
  }

  return {
    ...row,
    clave_componente: key,
    reactivo: preset.reactivo,
  };
};

const inferLegacyPrimerComponentKey = (row = {}) => {
  const normalizedReactivo = normalizeReactivoLabel(row?.reactivo);
  if (!normalizedReactivo) {
    return '';
  }

  if (normalizedReactivo.includes('fw')) {
    return 'primer_fw';
  }
  if (normalizedReactivo.includes('rv')) {
    return 'primer_rv';
  }
  if (normalizedReactivo.includes('sonda') || normalizedReactivo.includes('probe')) {
    return 'sonda_probe';
  }

  return '';
};

const isQuantitativeAssayType = (assayType = '') =>
  String(assayType || '').toLowerCase().includes('qpcr');

const isReverseTranscriptionAssayType = (assayType = '') =>
  String(assayType || '').toUpperCase().startsWith('RT');

const getProgramStageDefaults = (assayType = 'PCR') =>
  isQuantitativeAssayType(assayType) ? QPCR_PROGRAM_STAGES : PCR_PROGRAM_STAGES;

const hasProgramRowDetails = (row = {}) =>
  ['temperatura', 'tiempo', 'ciclos', 'observaciones'].some((key) => hasMeaningfulValue(row[key]));

const matchesProgramRowsTemplate = (rows = [], stages = []) =>
  rows.length === stages.length
  && rows.every((row, index) => row?.etapa === stages[index] && !hasProgramRowDetails(row));

const buildAssayViewConfig = (assayType = 'PCR') => {
  const normalizedType = String(assayType || 'PCR');
  const isQuantitative = isQuantitativeAssayType(normalizedType);
  const includesRT = isReverseTranscriptionAssayType(normalizedType);
  const familyKey = isQuantitative ? 'qpcr' : 'pcr';
  const familyLabel = isQuantitative ? 'qPCR' : 'PCR';
  const displayLabel = normalizedType || familyLabel;

  return {
    key: familyKey,
    familyLabel,
    displayLabel,
    includesRT,
    isQuantitative,
    stepDescriptions: {
      reactivos: isQuantitative
        ? 'Primers, mezcla maestra, controles y lectura Ct/Cq.'
        : 'Primers, mezcla maestra, controles y lectura por banda.',
      corrida: isQuantitative
        ? 'Pozos, Ct/Cq y programa de fluorescencia.'
        : 'Tubos, resultado por banda y programa de amplificacion.',
    },
    prepKicker: isQuantitative ? 'Vista qPCR' : 'Vista PCR',
    prepBadge: isQuantitative ? 'Lectura cuantitativa' : 'Lectura por amplicon',
    prepTitle: isQuantitative ? 'Preparacion guiada para qPCR' : 'Preparacion guiada para PCR convencional',
    prepDescription: isQuantitative
      ? 'La vista qPCR prioriza mezcla maestra, pozos, controles y lectura de Ct/Cq.'
      : 'La vista PCR prioriza mezcla maestra, tubos, controles y registro final por banda o amplicon.',
    prepPoints: isQuantitative
      ? [
          'Planifica placa o pozos antes de correr la amplificacion.',
          'Registra Ct/Cq e interpretacion de curva por muestra.',
          'Controla NTC sin senal y positivo con amplificacion esperada.',
        ]
      : [
          'Planifica un tubo por muestra y los controles de la corrida.',
          'Registra el resultado observado por banda, amplicon o visualizacion.',
          'Deja trazabilidad del control positivo y del NTC.',
        ],
    mixPlanningTitle: isQuantitative ? 'Plan de mezcla para qPCR' : 'Plan de mezcla para PCR convencional',
    mixPlanningDescription: isQuantitative
      ? 'Define una mezcla maestra para corrida cuantitativa y calcula volumenes por pozo.'
      : 'Define una mezcla maestra para PCR convencional y calcula volumenes por tubo.',
    primerTitle: isQuantitative ? 'Primers, sondas y reactivos de deteccion' : 'Primers y reactivos de trabajo',
    primerDescription: isQuantitative
      ? 'Carga primers, sonda o probe y concentraciones de trabajo para la corrida cuantitativa.'
      : 'Carga primers y reactivos de trabajo con lote y concentraciones para la corrida convencional.',
    controlsDescription: isQuantitative
      ? 'Resume el mix base y define que controles entran en la placa o corrida cuantitativa.'
      : 'Resume el mix base y define que controles entran en la corrida convencional.',
    mixTitle: isQuantitative ? 'Mix de reaccion para qPCR' : 'Mix de reaccion para PCR',
    mixDescription: isQuantitative
      ? 'Carga concentraciones y volumen por pozo. El sistema completa factor, volumen total y agua.'
      : 'Carga concentraciones y volumen por tubo. El sistema completa factor, volumen total y agua.',
    runPlanTitle: isQuantitative ? 'Plan de pozos sugerido' : 'Plan de tubos sugerido',
    runPlanDescription: isQuantitative
      ? 'Se propone un pozo por muestra y por control activo para facilitar la corrida cuantitativa.'
      : 'Se propone un tubo por muestra y por control activo para facilitar la corrida convencional.',
    runPositionLabel: isQuantitative ? 'Pozo / tubo' : 'N. tubo / pozo',
    runPositionPlaceholder: isQuantitative ? 'Ej. A1, B1, 01' : 'Ej. 01, 02, A1',
    runSamplesLabel: isQuantitative ? 'Pozos de muestra' : 'Tubos de muestra',
    runControlsLabel: isQuantitative ? 'Pozos de control' : 'Tubos de control',
    runTotalLabel: isQuantitative ? 'Total sugerido' : 'Total sugerido',
    runTitle: isQuantitative ? 'Pozos, controles y lectura de qPCR' : 'Tubos, controles y lectura final',
    runDescription: isQuantitative
      ? 'Completa pozo, Ct/Cq e interpretacion de curva. Los pozos vinculados se generan automaticamente.'
      : 'Completa numero de tubo, resultado e interpretacion final. Los tubos vinculados se generan automaticamente.',
    runResultLabel: isQuantitative ? 'Ct / Cq' : 'Resultado / banda',
    runResultPlaceholder: isQuantitative ? 'Ej. 22.8, ND, invalido' : 'Ej. 249 pb, banda tenue, negativo',
    runInterpretationLabel: isQuantitative ? 'Curva / interpretacion' : 'Interpretacion / amplicon',
    runInterpretationPlaceholder: isQuantitative
      ? 'Ej. Curva sigmoidea, Positivo'
      : 'Ej. Positivo por banda esperada',
    readoutTitle: isQuantitative ? 'Checklist de lectura qPCR' : 'Checklist de lectura PCR',
    readoutDescription: isQuantitative
      ? 'Usa esta vista para dejar trazabilidad de Ct/Cq, curva y controles de corrida.'
      : 'Usa esta vista para dejar trazabilidad de la banda observada, el amplicon y los controles.',
    readoutPoints: isQuantitative
      ? [
          'Carga Ct/Cq cuando haya amplificacion y ND cuando no haya senal.',
          'Usa interpretacion para curva, repeticion o validacion de la muestra.',
          'Confirma que el NTC no amplifique y que el positivo tenga la senal esperada.',
        ]
      : [
          'Carga en resultado la banda, el tamano esperado o el hallazgo observado.',
          'Usa interpretacion para positivo, negativo, tenue, repetir o invalido.',
          'Confirma que el control positivo y el NTC se comporten como se espera.',
        ],
    programTitle: isQuantitative ? 'Programa de amplificacion qPCR' : 'Programa de PCR y visualizacion',
    programDescription: isQuantitative
      ? 'Detalla activacion, ciclos y lectura de fluorescencia para la corrida cuantitativa.'
      : 'Detalla activacion, ciclos y condiciones previas a la visualizacion del amplicon.',
    closureDescription: isQuantitative
      ? 'Completa la interpretacion general de Ct/Cq, curva y conformidad de la corrida.'
      : 'Completa la interpretacion general de banda, amplicon y conformidad de la corrida.',
    sidebarDescription: isQuantitative
      ? 'Vista qPCR activa: mezcla, pozos y lectura cuantitativa.'
      : 'Vista PCR activa: mezcla, tubos y lectura final por amplicon.',
  };
};

const isNucleaseFreeWaterRow = (row = {}) => getFixedMixComponentKey(row) === 'agua_libre_nucleasas';

const isPrimerMixRow = (row = {}) => {
  const componentKey = getFixedMixComponentKey(row);
  return componentKey === 'primer_fw' || componentKey === 'primer_rv' || componentKey === 'sonda_probe';
};

const isForwardOrReversePrimerMixRow = (row = {}) => {
  const componentKey = getFixedMixComponentKey(row);
  return componentKey === 'primer_fw' || componentKey === 'primer_rv';
};

const sortMixReactionRows = (rows = []) => {
  const normalizedRows = rows.map((row, index) => ({
    ...applyFixedMixComponentPreset({
      ...row,
      clave_componente: getFixedMixComponentKey(row),
    }),
    __sortIndex: index,
  }));

  return normalizedRows
    .sort((left, right) => {
      const leftOrder = isNucleaseFreeWaterRow(left) ? Number.MAX_SAFE_INTEGER : left.__sortIndex;
      const rightOrder = isNucleaseFreeWaterRow(right) ? Number.MAX_SAFE_INTEGER : right.__sortIndex;
      return leftOrder - rightOrder;
    })
    .map(({ __sortIndex, ...row }) => row);
};

const mergeLegacyPrimerRowsIntoMix = (mixRows = [], primerRows = []) => {
  const nextMixRows = mixRows.map((row) => ({ ...row }));

  primerRows.forEach((primerRow) => {
    const componentKey = inferLegacyPrimerComponentKey(primerRow);
    if (!componentKey) {
      return;
    }

    const matchingIndex = nextMixRows.findIndex((row) => getFixedMixComponentKey(row) === componentKey);
    const nextValues = {
      identificacion_lote: primerRow?.identificacion_lote || '',
      concentracion_inicial: primerRow?.concentracion_stock || '',
      concentracion_final: primerRow?.concentracion_uso || '',
    };

    if (matchingIndex >= 0) {
      const currentRow = nextMixRows[matchingIndex];
      nextMixRows[matchingIndex] = {
        ...currentRow,
        clave_componente: componentKey,
        reactivo: FIXED_MIX_COMPONENT_BY_KEY[componentKey].reactivo,
        identificacion_lote: currentRow.identificacion_lote || nextValues.identificacion_lote,
        concentracion_inicial: currentRow.concentracion_inicial || nextValues.concentracion_inicial,
        concentracion_final: currentRow.concentracion_final || nextValues.concentracion_final,
      };
      return;
    }

    nextMixRows.push(createMixRow({
      clave_componente: componentKey,
      reactivo: FIXED_MIX_COMPONENT_BY_KEY[componentKey].reactivo,
      ...nextValues,
    }));
  });

  return nextMixRows;
};

const createDefaultMixRows = () =>
  sortMixReactionRows(DEFAULT_MIX_LABELS.map((label) => createMixRow({ reactivo: label })));

const createDefaultProgramRows = (assayType = 'PCR') =>
  getProgramStageDefaults(assayType).map((label) => createProgramRow({ etapa: label }));

const buildSampleMaterialLabel = (sample = {}) =>
  sample?.otro_tipo_muestra
  || sample?.tipo_muestra_sangre
  || sample?.tipo_muestra_animal
  || sample?.tipo_muestra_vegetal
  || sample?.tipo_muestra
  || '';

const uniqueValues = (values = []) => {
  const unique = [];
  values.forEach((value) => {
    if (!value || unique.includes(value)) {
      return;
    }
    unique.push(value);
  });
  return unique;
};

const clampInteger = (value, minimum, maximum, fallback) => {
  if (!Number.isInteger(value)) {
    return fallback;
  }

  return Math.min(maximum, Math.max(minimum, value));
};

const normalizePlateConfig = (config = {}) => {
  const rowCount = clampInteger(
    parseNumericValue(config.plate_rows),
    1,
    ALL_PLATE_ROW_LABELS.length,
    Number(DEFAULT_PLATE_CONFIG.plate_rows)
  );
  const columnCount = clampInteger(
    parseNumericValue(config.plate_columns),
    1,
    MAX_PLATE_COLUMN_COUNT,
    Number(DEFAULT_PLATE_CONFIG.plate_columns)
  );
  const plateCount = clampInteger(
    parseNumericValue(config.plate_count),
    1,
    MAX_PLATE_COUNT,
    Number(DEFAULT_PLATE_CONFIG.plate_count)
  );

  return {
    plate_rows: String(rowCount),
    plate_columns: String(columnCount),
    plate_count: String(plateCount),
    rowCount,
    columnCount,
    plateCount,
    rowLabels: ALL_PLATE_ROW_LABELS.slice(0, rowCount),
    positionsPerPlate: rowCount * columnCount,
    totalPositions: rowCount * columnCount * plateCount,
  };
};

const buildPlateConfigSummary = (plateConfig = DEFAULT_PLATE_CONFIG) => {
  const normalizedPlateConfig = normalizePlateConfig(plateConfig);
  const sizeLabel = `${normalizedPlateConfig.rowCount} x ${normalizedPlateConfig.columnCount}`;

  return normalizedPlateConfig.plateCount > 1
    ? `${normalizedPlateConfig.plateCount} placas de ${sizeLabel}`
    : `1 placa de ${sizeLabel}`;
};

const buildPlatePositionLabel = (plateNumber, rowLabel, columnNumber) =>
  plateNumber > 1 ? `P${plateNumber}:${rowLabel}${columnNumber}` : `${rowLabel}${columnNumber}`;

const buildPlatePositionFromIndex = (index, plateConfig = DEFAULT_PLATE_CONFIG) => {
  const normalizedPlateConfig = normalizePlateConfig(plateConfig);
  if (!Number.isInteger(index) || index < 0 || index >= normalizedPlateConfig.totalPositions) {
    return '';
  }

  const plateNumber = Math.floor(index / normalizedPlateConfig.positionsPerPlate) + 1;
  const plateIndex = index % normalizedPlateConfig.positionsPerPlate;
  const rowLabel = normalizedPlateConfig.rowLabels[
    Math.floor(plateIndex / normalizedPlateConfig.columnCount)
  ];
  const columnNumber = (plateIndex % normalizedPlateConfig.columnCount) + 1;

  return buildPlatePositionLabel(plateNumber, rowLabel, columnNumber);
};

const parsePlatePosition = (value = '', plateConfig = DEFAULT_PLATE_CONFIG) => {
  const normalizedPlateConfig = normalizePlateConfig(plateConfig);
  const cleanedValue = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
  if (!cleanedValue) {
    return null;
  }

  const plateMatch = cleanedValue.match(/^(?:P([1-2])[:-])?([A-H])0*([1-9]\d*)$/);
  if (plateMatch) {
    const plateNumber = Number(plateMatch[1] || 1);
    const rowLabel = plateMatch[2];
    const columnNumber = Number(plateMatch[3]);
    if (!Number.isInteger(columnNumber) || columnNumber < 1 || columnNumber > MAX_PLATE_COLUMN_COUNT) {
      return null;
    }
    const rowIndex = ALL_PLATE_ROW_LABELS.indexOf(rowLabel);
    const index = ((plateNumber - 1) * normalizedPlateConfig.positionsPerPlate)
      + (rowIndex * normalizedPlateConfig.columnCount)
      + (columnNumber - 1);

    return {
      normalized: buildPlatePositionLabel(plateNumber, rowLabel, columnNumber),
      basePosition: `${rowLabel}${columnNumber}`,
      plateNumber,
      rowLabel,
      rowIndex,
      columnNumber,
      index,
      isWithinConfiguredBounds: plateNumber <= normalizedPlateConfig.plateCount
        && rowIndex < normalizedPlateConfig.rowCount
        && columnNumber <= normalizedPlateConfig.columnCount,
    };
  }

  if (/^\d+$/.test(cleanedValue)) {
    const numericPosition = Number(cleanedValue);
    if (numericPosition >= 1 && numericPosition <= normalizedPlateConfig.totalPositions) {
      const normalized = buildPlatePositionFromIndex(numericPosition - 1, normalizedPlateConfig);
      const parsedPosition = parsePlatePosition(normalized, normalizedPlateConfig);

      if (!parsedPosition) {
        return null;
      }

      return {
        ...parsedPosition,
        index: numericPosition - 1,
      };
    }
  }

  return null;
};

const normalizePlatePosition = (value = '', plateConfig = DEFAULT_PLATE_CONFIG) => {
  const parsedPosition = parsePlatePosition(value, plateConfig);
  if (parsedPosition) {
    return parsedPosition.normalized;
  }

  return String(value || '').trim().toUpperCase().replace(/\s+/g, '');
};

const findNextAvailablePlatePosition = (rows = [], plateConfig = DEFAULT_PLATE_CONFIG) => {
  const normalizedPlateConfig = normalizePlateConfig(plateConfig);
  const usedIndexes = new Set(
    rows
      .map((row) => parsePlatePosition(row?.pozo_tubo, normalizedPlateConfig))
      .filter((parsedPosition) => parsedPosition?.isWithinConfiguredBounds)
      .map((parsedPosition) => parsedPosition.index)
      .filter((index) => Number.isInteger(index))
  );

  for (let index = 0; index < normalizedPlateConfig.totalPositions; index += 1) {
    if (!usedIndexes.has(index)) {
      return buildPlatePositionFromIndex(index, normalizedPlateConfig);
    }
  }

  return String(rows.length + 1);
};

const buildCorridaPlateState = (rows = [], plateConfig = DEFAULT_PLATE_CONFIG) => {
  const normalizedPlateConfig = normalizePlateConfig(plateConfig);
  const byPosition = new Map();
  const overflowRows = [];

  rows.forEach((row) => {
    const parsedPosition = parsePlatePosition(row?.pozo_tubo, normalizedPlateConfig);
    if (!parsedPosition || !parsedPosition.isWithinConfiguredBounds) {
      overflowRows.push({
        row,
        reason: 'outside',
        label: row?.pozo_tubo || 'Sin posicion',
      });
      return;
    }

    const existingPosition = byPosition.get(parsedPosition.normalized);
    if (!existingPosition) {
      byPosition.set(parsedPosition.normalized, {
        row,
        hasConflict: false,
      });
      return;
    }

    existingPosition.hasConflict = true;
    overflowRows.push({
      row,
      reason: 'duplicate',
      label: parsedPosition.normalized,
    });
  });

  return {
    byPosition,
    overflowRows,
  };
};

const buildSelectedCaseProtocols = (assay = {}, defaults = {}) => {
  const defaultCases = Array.isArray(defaults.casos) && defaults.casos.length > 0
    ? defaults.casos
    : defaults.protocolo_general
      ? [defaults.protocolo_general]
      : [];
  const primaryProtocol = assay.protocolo_general || defaults.protocolo_general || '';
  const selectedCaseProtocols = Array.isArray(assay.casos) && assay.casos.length > 0
    ? assay.casos
    : primaryProtocol
      ? [primaryProtocol]
      : defaultCases;
  const uniqueProtocols = uniqueValues(selectedCaseProtocols);

  if (primaryProtocol && uniqueProtocols.includes(primaryProtocol)) {
    return [primaryProtocol, ...uniqueProtocols.filter((protocol) => protocol !== primaryProtocol)];
  }

  return uniqueProtocols;
};

const sortSamplesByCaseSelection = (samples = [], selectedCaseProtocols = []) => {
  const caseOrder = new Map(selectedCaseProtocols.map((protocol, index) => [protocol, index]));

  return [...samples].sort((left, right) => {
    const leftOrder = caseOrder.get(left.protocolo_general) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = caseOrder.get(right.protocolo_general) ?? Number.MAX_SAFE_INTEGER;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return String(left.protocolo_muestra || '').localeCompare(String(right.protocolo_muestra || ''));
  });
};

const getCorridaRowIdentity = (row = {}, selectedSampleProtocols = new Set()) => {
  if (row.tipo_registro === 'CONTROL') {
    if (row.control_nombre) {
      return `control:${row.control_nombre}`;
    }
    if (row.identificacion_muestra) {
      return `control:${row.identificacion_muestra}`;
    }
    return null;
  }

  if (row.muestra_asociada) {
    return `sample:${row.muestra_asociada}`;
  }

  if (selectedSampleProtocols.has(row.identificacion_muestra)) {
    return `sample:${row.identificacion_muestra}`;
  }

  return null;
};

const serializeCorridaRowsForComparison = (rows = []) =>
  JSON.stringify(
    rows.map(({ id, ...rest }) => rest)
  );

const syncCorridaRowsWithPlan = (
  existingRows = [],
  selectedSamples = [],
  controls = [],
  plateConfig = DEFAULT_PLATE_CONFIG
) => {
  const normalizedPlateConfig = normalizePlateConfig(plateConfig);
  const manualRowsWithoutIdentity = [];
  const existingRowsByIdentity = new Map();
  const selectedSampleProtocols = new Set(
    selectedSamples.map((sample) => String(sample?.protocolo_muestra || '').trim()).filter(Boolean)
  );
  const usedPlateIndexes = new Set(
    existingRows
      .map((row) => parsePlatePosition(row?.pozo_tubo, normalizedPlateConfig))
      .filter((parsedPosition) => parsedPosition?.isWithinConfiguredBounds)
      .map((parsedPosition) => parsedPosition.index)
      .filter((index) => Number.isInteger(index))
  );

  existingRows.forEach((row) => {
    const identity = getCorridaRowIdentity(row, selectedSampleProtocols);
    if (identity && !existingRowsByIdentity.has(identity)) {
      existingRowsByIdentity.set(identity, row);
      return;
    }

    if (!identity) {
      manualRowsWithoutIdentity.push(row);
    }
  });

  const reserveNextPlatePosition = () => {
    for (let index = 0; index < normalizedPlateConfig.totalPositions; index += 1) {
      if (!usedPlateIndexes.has(index)) {
        usedPlateIndexes.add(index);
        return buildPlatePositionFromIndex(index, normalizedPlateConfig);
      }
    }

    return String(existingRows.length + usedPlateIndexes.size + 1);
  };

  const resolveTubePosition = (currentValue = '') => {
    if (hasMeaningfulValue(currentValue)) {
      const normalizedValue = normalizePlatePosition(currentValue, normalizedPlateConfig);
      const parsedPosition = parsePlatePosition(normalizedValue, normalizedPlateConfig);
      if (parsedPosition?.isWithinConfiguredBounds) {
        usedPlateIndexes.add(parsedPosition.index);
      }
      return normalizedValue;
    }

    return reserveNextPlatePosition();
  };

  const sampleRows = selectedSamples.map((sample) => {
    const currentRow = existingRowsByIdentity.get(`sample:${sample.protocolo_muestra}`);
    return createCorridaRow({
      ...currentRow,
      pozo_tubo: resolveTubePosition(currentRow?.pozo_tubo),
      identificacion_muestra: currentRow?.identificacion_muestra_editada
        ? currentRow?.identificacion_muestra || ''
        : '',
      identificacion_muestra_editada: Boolean(currentRow?.identificacion_muestra_editada),
      muestra_asociada: sample.protocolo_muestra,
      tipo_material: currentRow?.tipo_material || buildSampleMaterialLabel(sample),
      tipo_registro: 'MUESTRA',
      control_nombre: '',
      ct_resultado: currentRow?.ct_resultado || '',
      interpretacion: currentRow?.interpretacion || '',
    });
  });

  const controlRows = controls
    .filter((control) => control.estado === 'SI')
    .map((control) => {
      const currentRow = existingRowsByIdentity.get(`control:${control.nombre}`)
        || existingRowsByIdentity.get(`control:${buildControlRunLabel(control)}`);

      return createCorridaRow({
        ...currentRow,
        pozo_tubo: resolveTubePosition(currentRow?.pozo_tubo),
        identificacion_muestra: buildControlRunLabel(control),
        fecha_extraccion: '',
        tipo_material: 'Control',
        tipo_registro: 'CONTROL',
        control_nombre: control.nombre,
        ct_resultado: currentRow?.ct_resultado || '',
        interpretacion: currentRow?.interpretacion || '',
      });
    });

  return [...sampleRows, ...controlRows, ...manualRowsWithoutIdentity];
};

const getUserDisplayName = (user = {}) => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return fullName || user.username || user.email || 'Usuario sin nombre';
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return true;
};

const sanitizeRows = (rows = [], allowedKeys = []) =>
  rows
    .map((row) =>
      allowedKeys.reduce((accumulator, key) => {
        accumulator[key] = row?.[key] ?? '';
        return accumulator;
      }, {})
    )
    .filter((row) => allowedKeys.some((key) => hasMeaningfulValue(row[key])));

const normalizeDiagnosticTargets = (targets = [], fallbackTarget = '') => {
  const sourceTargets = Array.isArray(targets)
    ? targets
    : hasMeaningfulValue(targets)
      ? [targets]
      : [];
  const fallbackTargets = sourceTargets.length === 0 && hasMeaningfulValue(fallbackTarget)
    ? [fallbackTarget]
    : sourceTargets;
  const normalizedTargets = [];
  const seenTargets = new Set();

  fallbackTargets.forEach((target) => {
    const cleanedTarget = String(target || '').trim();
    if (!cleanedTarget) {
      return;
    }

    const normalizedKey = cleanedTarget.toLocaleLowerCase('es-AR');
    if (seenTargets.has(normalizedKey)) {
      return;
    }

    seenTargets.add(normalizedKey);
    normalizedTargets.push(cleanedTarget);
  });

  return normalizedTargets;
};

const buildDiagnosticTargetsSummary = (targets = [], fallbackTarget = '') =>
  normalizeDiagnosticTargets(targets, fallbackTarget).join('; ');

const buildPlateDiagnosticAssignments = (
  plateConfig = DEFAULT_PLATE_CONFIG,
  targets = [],
  fallbackTarget = ''
) => {
  const normalizedPlateConfig = normalizePlateConfig(plateConfig);
  const normalizedTargets = normalizeDiagnosticTargets(targets, fallbackTarget);

  if (normalizedPlateConfig.plateCount === 1) {
    return [{
      plateNumber: 1,
      diagnosticIndex: normalizedTargets.length <= 1 ? 1 : null,
      target: normalizedTargets.length <= 1
        ? (normalizedTargets[0] || '')
        : buildDiagnosticTargetsSummary(normalizedTargets),
      hasTarget: normalizedTargets.length > 0,
      isMultiplex: normalizedTargets.length > 1,
    }];
  }

  return Array.from({ length: normalizedPlateConfig.plateCount }, (_, index) => ({
    plateNumber: index + 1,
    diagnosticIndex: index + 1,
    target: normalizedTargets[index] || '',
    hasTarget: hasMeaningfulValue(normalizedTargets[index]),
    isMultiplex: false,
  }));
};

const buildLegacyPrimerRowsFromMixRows = (rows = []) =>
  rows
    .filter((row) => isPrimerMixRow(row))
    .map((row) => ({
      reactivo: row.reactivo || '',
      identificacion_lote: row.identificacion_lote || '',
      concentracion_stock: row.concentracion_inicial || '',
      concentracion_uso: row.concentracion_final || '',
    }))
    .filter((row) =>
      ['identificacion_lote', 'concentracion_stock', 'concentracion_uso'].some((key) =>
        hasMeaningfulValue(row[key])
      )
    );

const normalizeDynamicRows = (fieldName, rows = []) =>
  fieldName === 'mix_reaccion' ? sortMixReactionRows(rows) : rows;

const calculateMixPlanning = (data = {}) => {
  const config = createDefaultMixConfig(data.configuracion_mix || {});
  const sampleCount = Array.isArray(data.muestras) ? data.muestras.length : 0;
  const controlCount = Array.isArray(data.controles)
    ? data.controles.filter((control) => control.estado === 'SI').length
    : 0;
  const extraReactionsRaw = parseNumericValue(config.reacciones_extra);
  const extraReactions = extraReactionsRaw !== null && extraReactionsRaw > 0 ? extraReactionsRaw : 0;
  const targetVolumeRaw = parseNumericValue(config.volumen_reaccion_objetivo);
  const targetVolume = targetVolumeRaw !== null && targetVolumeRaw > 0 ? targetVolumeRaw : null;
  const overageFactorRaw = parseNumericValue(config.factor_sobrepreparacion);
  const hasValidOverageFactor = overageFactorRaw !== null && overageFactorRaw > 0;
  const overageFactor = overageFactorRaw !== null && overageFactorRaw > 0 ? overageFactorRaw : 1;
  const totalPlannedReactions = sampleCount + controlCount + extraReactions;
  const totalCalculatedReactions = totalPlannedReactions > 0
    ? totalPlannedReactions * overageFactor
    : 0;
  const reactionRows = Array.isArray(data.mix_reaccion) ? data.mix_reaccion : [];
  const nonWaterPerTubeTotal = reactionRows.reduce((accumulator, row) => {
    if (isNucleaseFreeWaterRow(row)) {
      return accumulator;
    }

    const volume = parseNumericValue(row.volumen_por_tubo);
    return volume === null ? accumulator : accumulator + volume;
  }, 0);
  const waterPerTube = targetVolume !== null ? targetVolume - nonWaterPerTubeTotal : null;
  const waterIsCalculated = waterPerTube !== null;
  const computedRows = sortMixReactionRows(
    reactionRows.map((row) => {
      const isWaterRow = isNucleaseFreeWaterRow(row);
      const volumePerTube = isWaterRow ? waterPerTube : parseNumericValue(row.volumen_por_tubo);
      const explicitTubeCount = parseNumericValue(row.cantidad_tubos);
      const effectiveTubeCount = explicitTubeCount !== null && explicitTubeCount > 0
        ? explicitTubeCount
        : totalPlannedReactions > 0
          ? totalPlannedReactions
          : null;
      const totalFactorLabel = effectiveTubeCount !== null
        ? `${formatDecimalValue(effectiveTubeCount)} x ${formatDecimalValue(overageFactor)}`
        : '';
      const totalVolume = volumePerTube !== null && effectiveTubeCount !== null
        ? volumePerTube * effectiveTubeCount * overageFactor
        : null;

      return {
        ...row,
        reactivo: isWaterRow ? NUCLEASE_FREE_WATER_LABEL : row.reactivo,
        cantidad_tubos: effectiveTubeCount !== null ? formatDecimalValue(effectiveTubeCount) : row.cantidad_tubos,
        numero_tubos_factor: totalFactorLabel || row.numero_tubos_factor || '',
        volumen_por_tubo: isWaterRow && waterIsCalculated ? formatDecimalValue(waterPerTube) : row.volumen_por_tubo,
        volumen_total: totalVolume !== null ? formatDecimalValue(totalVolume) : row.volumen_total,
      };
    })
  );

  return {
    config,
    sampleCount,
    controlCount,
    extraReactions,
    targetVolume,
    overageFactor,
    hasValidOverageFactor,
    totalPlannedReactions,
    totalCalculatedReactions,
    waterPerTube,
    waterIsCalculated,
    negativeWater: waterPerTube !== null && waterPerTube < 0,
    computedRows,
  };
};

const normalizeAssayData = (assay = {}, defaults = {}) => {
  const savedControls = Array.isArray(assay.reactivos_controles?.controles)
    ? assay.reactivos_controles.controles
    : [];
  const savedPrimerRows = Array.isArray(assay.primers_sondas) && assay.primers_sondas.length > 0
    ? assay.primers_sondas.map((row) => createPrimerRow(row))
    : [];
  const baseMixRows = Array.isArray(assay.mix_reaccion) && assay.mix_reaccion.length > 0
    ? assay.mix_reaccion.map((row) => createMixRow(row))
    : createDefaultMixRows();
  const selectedCaseProtocols = buildSelectedCaseProtocols(assay, defaults);
  const selectedSampleProtocols =
    Array.isArray(assay.muestras) && assay.muestras.length > 0
      ? assay.muestras
      : assay.muestra
        ? [assay.muestra]
        : defaults.muestras || [];
  const normalizedAssayType = assay.tipo_ensayo || defaults.tipo_ensayo || 'PCR';
  const normalizedMatrixTissue = assay.matriz_tejido || '';
  const normalizedDiagnosticTargets = normalizeDiagnosticTargets(
    assay.patogenos_blanco,
    assay.patogeno_blanco || defaults.patogeno_blanco || ''
  );

  return {
    ensayo_numero: assay.ensayo_numero || '',
    protocolo_general: selectedCaseProtocols[0] || assay.protocolo_general || defaults.protocolo_general || '',
    casos: selectedCaseProtocols,
    muestras: selectedSampleProtocols,
    tipo_ensayo: normalizedAssayType,
    responsable: assay.responsable || '',
    fecha_ensayo: assay.fecha_ensayo || getTodayInputValue(),
    origen_muestras: assay.origen_muestras || '',
    matriz_tejido: getCanonicalMatrixTissueOption(normalizedMatrixTissue) || normalizedMatrixTissue,
    numero_muestras: assay.numero_muestras || 1,
    patogeno_blanco: buildDiagnosticTargetsSummary(normalizedDiagnosticTargets),
    patogenos_blanco: normalizedDiagnosticTargets.length > 0 ? normalizedDiagnosticTargets : [''],
    primers_sondas: savedPrimerRows.length > 0 ? savedPrimerRows : [createPrimerRow()],
    reactivos_base: assay.reactivos_controles?.reactivos_base || '',
    controles: createControls(savedControls),
    configuracion_placa: createDefaultPlateConfig(assay.reactivos_controles?.configuracion_placa || {}),
    configuracion_mix: createDefaultMixConfig(assay.reactivos_controles?.configuracion_mix || {}),
    mix_reaccion: sortMixReactionRows(mergeLegacyPrimerRowsIntoMix(baseMixRows, savedPrimerRows)),
    corrida_muestras:
      Array.isArray(assay.corrida_muestras) && assay.corrida_muestras.length > 0
        ? assay.corrida_muestras.map((row) =>
            createCorridaRow({
              ...row,
              identificacion_muestra_editada: hasMeaningfulValue(row?.identificacion_muestra),
            })
          )
        : selectedSampleProtocols.length > 0
          ? selectedSampleProtocols.map((protocol) =>
              createCorridaRow({
                muestra_asociada: protocol,
              })
            )
          : [createCorridaRow()],
    programa_termociclador:
      Array.isArray(assay.programa_termociclador) && assay.programa_termociclador.length > 0
        ? assay.programa_termociclador.map((row) => createProgramRow(row))
        : createDefaultProgramRows(normalizedAssayType),
    observaciones_corrida: assay.observaciones_corrida || '',
    resultado_conforme: assay.resultado_conforme || '',
    validado_por: assay.validado_por || '',
    fecha_validacion: assay.fecha_validacion || '',
    archivo_adjunto: assay.archivo_adjunto || '',
  };
};

const DynamicRowsSection = ({
  title,
  description,
  rows,
  columns,
  addLabel,
  emptyState,
  onAdd,
  onChange,
  onRemove,
  canRemoveRow,
  nonRemovableLabel = 'Vinculado al plan',
}) => (
  <section className="result-summary-card molecular-card-section">
    <div className="molecular-card-section__top">
      <div>
        <h4 className="result-summary-card__title">{title}</h4>
        <p className="result-summary-card__text">{description}</p>
      </div>
      <button type="button" className="btn btn-outline-brand btn-sm" onClick={onAdd}>
        <FiPlus /> {addLabel}
      </button>
    </div>

    {rows.length === 0 ? (
      <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">{emptyState}</div>
    ) : (
      <div className="molecular-dynamic-list">
        {rows.map((row, index) => (
          <article key={row.id} className="molecular-dynamic-row">
            <div className="molecular-dynamic-row__header">
              {(() => {
                const isRemovable = typeof canRemoveRow === 'function' ? canRemoveRow(row, index) : true;
                return (
                  <>
              <strong>Componente {index + 1}</strong>
                    {isRemovable ? (
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => onRemove(row.id)}>
                        <FiTrash2 /> Quitar
                      </button>
                    ) : (
                      <span className="molecular-row-badge">{nonRemovableLabel}</span>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="molecular-dynamic-row__grid">
              {columns.map((column) => {
                const isReadOnly =
                  typeof column.readOnly === 'function' ? column.readOnly(row) : Boolean(column.readOnly);
                const resolvedLabel =
                  typeof column.label === 'function' ? column.label(row) : column.label;
                const resolvedPlaceholder =
                  typeof column.placeholder === 'function' ? column.placeholder(row) : (column.placeholder || '');
                const resolvedHelperText =
                  typeof column.helperText === 'function' ? column.helperText(row) : column.helperText;

                return (
                  <label className={`molecular-field ${column.wide ? 'molecular-field--wide' : ''}`} key={column.key}>
                    <span className="form-label">{resolvedLabel}</span>
                    {column.type === 'select' ? (
                      <select
                        className="form-select"
                        value={row[column.key] || ''}
                        onChange={(event) => onChange(row.id, column.key, event.target.value)}
                        disabled={isReadOnly}
                      >
                        {(column.options || []).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : column.type === 'textarea' ? (
                      <textarea
                        className="form-control"
                        rows={column.rows || 3}
                        value={row[column.key] || ''}
                        placeholder={resolvedPlaceholder}
                        onChange={(event) => onChange(row.id, column.key, event.target.value)}
                        readOnly={isReadOnly}
                      />
                    ) : (
                      <input
                        type={column.type || 'text'}
                        className="form-control"
                        value={row[column.key] || ''}
                        placeholder={resolvedPlaceholder}
                        onChange={(event) => onChange(row.id, column.key, event.target.value)}
                        readOnly={isReadOnly}
                      />
                    )}
                    {resolvedHelperText ? <span className="form-text mt-1">{resolvedHelperText}</span> : null}
                  </label>
                );
              })}
            </div>
          </article>
        ))}
      </div>
    )}
  </section>
);

const CorridaRowDropdown = ({
  row,
  assayView,
  plateConfig,
  plateDiagnosticAssignments = [],
  onChange,
  onRemove,
  onClose,
  canRemove,
  nonRemovableLabel,
}) => {
  const normalizedPlateConfig = normalizePlateConfig(plateConfig);
  const lastConfiguredRow = normalizedPlateConfig.rowLabels[normalizedPlateConfig.rowLabels.length - 1] || 'H';
  const singlePlateExample = `${lastConfiguredRow}${normalizedPlateConfig.columnCount}`;
  const parsedPosition = parsePlatePosition(row.pozo_tubo, normalizedPlateConfig);
  const rowPlateNumber = parsedPosition?.plateNumber || 1;
  const plateDiagnosticAssignment = plateDiagnosticAssignments.find(
    (assignment) => assignment.plateNumber === rowPlateNumber
  ) || null;

  return (
    <article className="molecular-plate-dropdown-card">
      <div className="molecular-dynamic-row__header">
        <div className="molecular-plate-dropdown-header">
          <strong>
            Editar {normalizePlatePosition(row.pozo_tubo, normalizedPlateConfig) || row.pozo_tubo || 'Sin posicion'}
          </strong>
          {plateDiagnosticAssignment ? (
            <span className={`molecular-plate-dropdown-subtitle${
              plateDiagnosticAssignment.hasTarget ? '' : ' molecular-plate-dropdown-subtitle--warning'
            }`}>
              {plateDiagnosticAssignment.hasTarget
                ? plateDiagnosticAssignment.isMultiplex
                  ? `Placa ${rowPlateNumber} · Multiplex: ${plateDiagnosticAssignment.target}`
                  : `Placa ${rowPlateNumber} · Diagnostico ${plateDiagnosticAssignment.diagnosticIndex}: ${plateDiagnosticAssignment.target}`
                : `Placa ${rowPlateNumber} · Diagnostico ${plateDiagnosticAssignment.diagnosticIndex} sin cargar`}
            </span>
          ) : null}
        </div>
        <div className="molecular-plate-dropdown-actions">
          {canRemove ? (
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => onRemove(row.id)}>
              <FiTrash2 /> Quitar
            </button>
          ) : (
            <span className="molecular-row-badge">{nonRemovableLabel}</span>
          )}
          <button type="button" className="btn btn-outline-brand btn-sm" onClick={onClose}>
            <FiX /> Cerrar
          </button>
        </div>
      </div>

      <div className="molecular-dynamic-row__grid">
        <label className="molecular-field">
          <span className="form-label">{assayView.runPositionLabel}</span>
          <input
            type="text"
            className="form-control"
            value={row.pozo_tubo || ''}
            placeholder={assayView.runPositionPlaceholder}
            onChange={(event) => onChange(row.id, 'pozo_tubo', event.target.value)}
          />
          <span className="form-text mt-1">
            {normalizedPlateConfig.plateCount > 1
              ? `Ej. A1, ${singlePlateExample} o P2:A1`
              : `Ej. A1, B4 o ${singlePlateExample}`}
          </span>
        </label>

      <label className="molecular-field">
        <span className="form-label">ID muestra / protocolo</span>
        <input
          type="text"
          className="form-control"
          value={row.identificacion_muestra || ''}
          placeholder="Ej. BM-014 o codigo interno"
          onChange={(event) => onChange(row.id, 'identificacion_muestra', event.target.value)}
          readOnly={row.tipo_registro === 'CONTROL' && !canRemove}
        />
      </label>

      <label className="molecular-field">
        <span className="form-label">Fecha extracción</span>
        <input
          type="date"
          className="form-control"
          value={row.fecha_extraccion || ''}
          onChange={(event) => onChange(row.id, 'fecha_extraccion', event.target.value)}
        />
      </label>

      <label className="molecular-field">
        <span className="form-label">Tipo material</span>
        <input
          type="text"
          className="form-control"
          value={row.tipo_material || ''}
          placeholder="Ej. hisopo, suero, tejido"
          onChange={(event) => onChange(row.id, 'tipo_material', event.target.value)}
        />
      </label>

      <label className="molecular-field">
        <span className="form-label">Control / muestra</span>
        <select
          className="form-select"
          value={row.tipo_registro || 'MUESTRA'}
          onChange={(event) => onChange(row.id, 'tipo_registro', event.target.value)}
          disabled={!canRemove}
        >
          <option value="MUESTRA">Muestra</option>
          <option value="CONTROL">Control</option>
        </select>
      </label>

      <label className="molecular-field">
        <span className="form-label">{assayView.runResultLabel}</span>
        <input
          type="text"
          className="form-control"
          value={row.ct_resultado || ''}
          placeholder={assayView.runResultPlaceholder}
          onChange={(event) => onChange(row.id, 'ct_resultado', event.target.value)}
        />
      </label>

      <label className="molecular-field molecular-field--wide">
        <span className="form-label">{assayView.runInterpretationLabel}</span>
        <textarea
          className="form-control"
          rows="2"
          value={row.interpretacion || ''}
          placeholder={assayView.runInterpretationPlaceholder}
          onChange={(event) => onChange(row.id, 'interpretacion', event.target.value)}
        />
      </label>
    </div>
  </article>
  );
};

const CorridaPlateSection = ({
  title,
  description,
  rows,
  assayView,
  plateConfig,
  plateDiagnosticAssignments = [],
  onPlateConfigChange,
  selectedRowId,
  onSelectRow,
  onAddRowAtPosition,
  onChange,
  onRemove,
  canRemoveRow,
  nonRemovableLabel = 'Vinculado al plan',
}) => {
  const normalizedPlateConfig = normalizePlateConfig(plateConfig);
  const { byPosition, overflowRows } = buildCorridaPlateState(rows, normalizedPlateConfig);
  const selectedRow = rows.find((row) => row.id === selectedRowId) || null;
  const selectedRowCanRemove = selectedRow
    ? (typeof canRemoveRow === 'function' ? canRemoveRow(selectedRow) : true)
    : true;
  const shouldShowFallbackEditor = false;
  const tableMinWidth = Math.max(360, (normalizedPlateConfig.columnCount * 84) + 72);
  const rowOptions = Array.from({ length: ALL_PLATE_ROW_LABELS.length }, (_, index) => String(index + 1));
  const columnOptions = Array.from({ length: MAX_PLATE_COLUMN_COUNT }, (_, index) => String(index + 1));
  const plateOptions = Array.from({ length: MAX_PLATE_COUNT }, (_, index) => String(index + 1));

  return (
    <section className="result-summary-card molecular-card-section">
      <div className="molecular-card-section__top">
        <div>
          <h4 className="result-summary-card__title">{title}</h4>
          <p className="result-summary-card__text">{description}</p>
        </div>
      </div>

      <div className="molecular-plate-config-grid">
        <label className="molecular-field">
          <span className="form-label">Filas</span>
          <select
            className="form-select"
            value={normalizedPlateConfig.plate_rows}
            onChange={(event) => onPlateConfigChange('plate_rows', event.target.value)}
          >
            {rowOptions.map((value) => (
              <option key={`plate-rows-${value}`} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>

        <label className="molecular-field">
          <span className="form-label">Columnas</span>
          <select
            className="form-select"
            value={normalizedPlateConfig.plate_columns}
            onChange={(event) => onPlateConfigChange('plate_columns', event.target.value)}
          >
            {columnOptions.map((value) => (
              <option key={`plate-columns-${value}`} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>

        <label className="molecular-field">
          <span className="form-label">Cantidad de placas</span>
          <select
            className="form-select"
            value={normalizedPlateConfig.plate_count}
            onChange={(event) => onPlateConfigChange('plate_count', event.target.value)}
          >
            {plateOptions.map((value) => (
              <option key={`plate-count-${value}`} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="form-text mt-2 mb-0">
        Configuracion actual: <strong>{buildPlateConfigSummary(normalizedPlateConfig)}</strong>. Si reduces la placa,
        los pozos fuera del rango quedan abajo para revisarlos.
      </p>

      <div className="molecular-plate-assignment-list">
        {plateDiagnosticAssignments.map((assignment) => (
          <div
            key={`plate-assignment-${assignment.plateNumber}`}
            className={`molecular-plate-assignment${
              assignment.hasTarget ? '' : ' molecular-plate-assignment--warning'
            }`}
          >
            <strong>Placa {assignment.plateNumber}</strong>
            <span>
              {assignment.hasTarget
                ? assignment.isMultiplex
                  ? `Multiplex: ${assignment.target}`
                  : `Diagnostico ${assignment.diagnosticIndex}: ${assignment.target}`
                : `Diagnostico ${assignment.diagnosticIndex} sin cargar`}
            </span>
          </div>
        ))}
      </div>

      {selectedRow ? (
        <div className="molecular-plate-editor-shell">
          <CorridaRowDropdown
            row={selectedRow}
            assayView={assayView}
            plateConfig={normalizedPlateConfig}
            plateDiagnosticAssignments={plateDiagnosticAssignments}
            onChange={onChange}
            onRemove={onRemove}
            onClose={() => onSelectRow('')}
            canRemove={selectedRowCanRemove}
            nonRemovableLabel={nonRemovableLabel}
          />
        </div>
      ) : null}

      <div className="molecular-plate-stack">
        {Array.from({ length: normalizedPlateConfig.plateCount }, (_, plateIndex) => {
          const plateNumber = plateIndex + 1;
          const plateKey = `plate-${plateNumber}`;
          const plateDiagnosticAssignment = plateDiagnosticAssignments.find(
            (assignment) => assignment.plateNumber === plateNumber
          ) || null;

          return (
            <div key={plateKey} className="molecular-plate-panel">
              <div className="molecular-plate-panel__header">
                <div className="molecular-plate-panel__title">
                  <strong>Placa {plateNumber}</strong>
                  {plateDiagnosticAssignment ? (
                    <span className={`molecular-plate-panel__diagnostic${
                      plateDiagnosticAssignment.hasTarget ? '' : ' molecular-plate-panel__diagnostic--warning'
                    }`}>
                      {plateDiagnosticAssignment.hasTarget
                        ? plateDiagnosticAssignment.isMultiplex
                          ? `Multiplex: ${plateDiagnosticAssignment.target}`
                          : `Diagnostico ${plateDiagnosticAssignment.diagnosticIndex}: ${plateDiagnosticAssignment.target}`
                        : `Diagnostico ${plateDiagnosticAssignment.diagnosticIndex} sin cargar`}
                    </span>
                  ) : null}
                </div>
                <span>{normalizedPlateConfig.rowCount} filas x {normalizedPlateConfig.columnCount} columnas</span>
              </div>

              <div className="molecular-plate-scroll">
                <table className="molecular-plate-table" style={{ minWidth: `${tableMinWidth}px` }}>
                  <thead>
                    <tr>
                      <th className="molecular-plate-axis molecular-plate-axis--corner" aria-hidden="true" />
                      {Array.from({ length: normalizedPlateConfig.columnCount }, (_, index) => (
                        <th key={`${plateKey}-col-${index + 1}`} className="molecular-plate-axis" scope="col">
                          {index + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {normalizedPlateConfig.rowLabels.map((rowLabel) => (
                      <tr key={`${plateKey}-row-${rowLabel}`}>
                        <th className="molecular-plate-axis molecular-plate-axis--row" scope="row">
                          {rowLabel}
                        </th>
                        {Array.from({ length: normalizedPlateConfig.columnCount }, (_, index) => {
                          const position = buildPlatePositionLabel(plateNumber, rowLabel, index + 1);
                          const displayPosition = `${rowLabel}${index + 1}`;
                          const cellData = byPosition.get(position);
                          const cellRow = cellData?.row || null;
                          const isSelected = Boolean(cellRow && cellRow.id === selectedRowId);
                          const isControl = cellRow?.tipo_registro === 'CONTROL';
                          const cellClasses = [
                            'molecular-plate-cell',
                            cellRow ? 'molecular-plate-cell--filled' : '',
                            isSelected ? 'molecular-plate-cell--selected' : '',
                            isControl ? 'molecular-plate-cell--control' : '',
                            cellData?.hasConflict ? 'molecular-plate-cell--conflict' : '',
                          ]
                            .filter(Boolean)
                            .join(' ');

                          return (
                            <td key={position} className="molecular-plate-slot">
                              <button
                                type="button"
                                className={cellClasses}
                                onClick={() => {
                                  if (cellRow) {
                                    onSelectRow(isSelected ? '' : cellRow.id);
                                    return;
                                  }
                                  onAddRowAtPosition(position);
                                }}
                              >
                                <span className="molecular-plate-cell__position">{displayPosition}</span>
                                {cellRow ? (
                                  <>
                                    <strong className="molecular-plate-cell__label">
                                      {cellRow.identificacion_muestra || cellRow.muestra_asociada || 'Sin ID'}
                                    </strong>
                                    <span className="molecular-plate-cell__meta">
                                      {isControl ? 'Control' : 'Muestra'}
                                    </span>
                                    <span className="molecular-plate-cell__meta molecular-plate-cell__meta--muted">
                                      {cellRow.ct_resultado || cellRow.interpretacion || 'Sin lectura'}
                                    </span>
                                    {cellData?.hasConflict ? (
                                      <span className="molecular-plate-cell__warning">Pozo duplicado</span>
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    <span className="molecular-plate-cell__empty">Libre</span>
                                    <span className="molecular-plate-cell__hint">Agregar fila</span>
                                  </>
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {shouldShowFallbackEditor ? (
        <article className="molecular-plate-editor">
          <div className="molecular-dynamic-row__header">
            <strong>
              Pozo seleccionado {normalizePlatePosition(selectedRow.pozo_tubo, normalizedPlateConfig) || selectedRow.pozo_tubo || 'Sin posicion'}
            </strong>
            {(typeof canRemoveRow === 'function' ? canRemoveRow(selectedRow) : true) ? (
              <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => onRemove(selectedRow.id)}>
                <FiTrash2 /> Quitar
              </button>
            ) : (
              <span className="molecular-row-badge">{nonRemovableLabel}</span>
            )}
          </div>

          <div className="molecular-dynamic-row__grid">
            <label className="molecular-field">
              <span className="form-label">{assayView.runPositionLabel}</span>
              <input
                type="text"
                className="form-control"
                value={selectedRow.pozo_tubo || ''}
                placeholder={assayView.runPositionPlaceholder}
                onChange={(event) => onChange(selectedRow.id, 'pozo_tubo', event.target.value)}
              />
            </label>

            <label className="molecular-field">
              <span className="form-label">ID muestra / protocolo</span>
              <input
                type="text"
                className="form-control"
                value={selectedRow.identificacion_muestra || ''}
                placeholder="Ej. BM-014 o codigo interno"
                onChange={(event) => onChange(selectedRow.id, 'identificacion_muestra', event.target.value)}
                readOnly={selectedRow.tipo_registro === 'CONTROL' && !(typeof canRemoveRow === 'function' ? canRemoveRow(selectedRow) : true)}
              />
            </label>

            <label className="molecular-field">
              <span className="form-label">Fecha extracción</span>
              <input
                type="date"
                className="form-control"
                value={selectedRow.fecha_extraccion || ''}
                onChange={(event) => onChange(selectedRow.id, 'fecha_extraccion', event.target.value)}
              />
            </label>

            <label className="molecular-field">
              <span className="form-label">Tipo material</span>
              <input
                type="text"
                className="form-control"
                value={selectedRow.tipo_material || ''}
                placeholder="Ej. hisopo, suero, tejido"
                onChange={(event) => onChange(selectedRow.id, 'tipo_material', event.target.value)}
              />
            </label>

            <label className="molecular-field">
              <span className="form-label">Control / muestra</span>
              <select
                className="form-select"
                value={selectedRow.tipo_registro || 'MUESTRA'}
                onChange={(event) => onChange(selectedRow.id, 'tipo_registro', event.target.value)}
                disabled={!(typeof canRemoveRow === 'function' ? canRemoveRow(selectedRow) : true)}
              >
                <option value="MUESTRA">Muestra</option>
                <option value="CONTROL">Control</option>
              </select>
            </label>

            <label className="molecular-field">
              <span className="form-label">{assayView.runResultLabel}</span>
              <input
                type="text"
                className="form-control"
                value={selectedRow.ct_resultado || ''}
                placeholder={assayView.runResultPlaceholder}
                onChange={(event) => onChange(selectedRow.id, 'ct_resultado', event.target.value)}
              />
            </label>

            <label className="molecular-field molecular-field--wide">
              <span className="form-label">{assayView.runInterpretationLabel}</span>
              <textarea
                className="form-control"
                rows="2"
                value={selectedRow.interpretacion || ''}
                placeholder={assayView.runInterpretationPlaceholder}
                onChange={(event) => onChange(selectedRow.id, 'interpretacion', event.target.value)}
              />
            </label>
          </div>
        </article>
      ) : (
        <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
          Selecciona un pozo de la grilla para editarlo o usa una celda libre para agregar una fila manual.
        </div>
      )}

      {overflowRows.length > 0 ? (
        <div className="alert alert-warning mt-3 mb-0">
          <FiAlertCircle /> Hay {overflowRows.length} fila(s) fuera de la configuracion actual o con pozo duplicado.
          <div className="molecular-plate-overflow-list mt-3">
            {overflowRows.map(({ row, reason, label }) => (
              <button
                key={row.id}
                type="button"
                className="molecular-plate-overflow-item"
                onClick={() => onSelectRow(selectedRowId === row.id ? '' : row.id)}
              >
                <strong>{row.identificacion_muestra || row.control_nombre || row.pozo_tubo || 'Fila manual'}</strong>
                <span>
                  {reason === 'duplicate'
                    ? `Conflicto en ${label}`
                    : `Posicion fuera de placa: ${label}`}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

const BiologiaMolecularForm = ({
  assayId = '',
  protocoloGeneralInicial = '',
  protocoloMuestraInicial = '',
  viewMode = 'workflow',
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const normalizedAssayId = normalizeAssayId(assayId);
  const isEditMode = Boolean(normalizedAssayId);
  const isClosureMode = viewMode === 'closure';
  const returnToCaseDetail = Boolean(location.state?.returnToCaseDetail);
  const workflowStepCount = ASSAY_STEPS.filter((step) => step.id !== CLOSURE_STEP_ID).length;
  const [currentStep, setCurrentStep] = useState(isClosureMode ? CLOSURE_STEP_ID : 1);
  const [furthestUnlockedStep, setFurthestUnlockedStep] = useState(1);
  const [caseOptions, setCaseOptions] = useState([]);
  const [availableSamples, setAvailableSamples] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingRecord, setLoadingRecord] = useState(isEditMode);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState(
    location.state?.saveMessage ? { type: 'success', text: location.state.saveMessage } : null
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCorridaRowId, setSelectedCorridaRowId] = useState('');
  const [formData, setFormData] = useState(() =>
    normalizeAssayData({}, {
      protocolo_general: protocoloGeneralInicial,
      casos: protocoloGeneralInicial ? [protocoloGeneralInicial] : [],
      muestras: protocoloMuestraInicial ? [protocoloMuestraInicial] : [],
    })
  );

  useEffect(() => {
    console.log(BM_DEBUG_PREFIX, 'Contexto inicial del formulario', {
      assayId,
      isClosureMode,
      isEditMode,
      normalizedAssayId,
      pathname: location.pathname,
      protocoloGeneralInicial,
      protocoloMuestraInicial,
      returnToCaseDetail,
      state: location.state,
      viewMode,
    });
  }, [
    assayId,
    isClosureMode,
    isEditMode,
    location.pathname,
    location.state,
    normalizedAssayId,
    protocoloGeneralInicial,
    protocoloMuestraInicial,
    returnToCaseDetail,
    viewMode,
  ]);

  useEffect(() => {
    let active = true;
    setLoadingBase(true);

    Promise.all([
      axios.get(apiUrl('/api/ingresos_casos/?compact=1')),
      axios.get(apiUrl('/api/usuarios/opciones/')),
    ])
      .then(([casesResponse, usersResponse]) => {
        if (!active) {
          return;
        }

        const protocols = Array.isArray(casesResponse.data)
          ? casesResponse.data.map((item) => item?.protocolo_general).filter(Boolean)
          : [];
        setCaseOptions(protocols);
        setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
      })
      .catch((error) => {
        console.error('Error al cargar catálogos de biología molecular:', error);
        if (!active) {
          return;
        }

        const message = 'No se pudieron cargar los casos o usuarios necesarios para el sector.';
        toast.error(message);
        setSaveNotice({ type: 'error', text: message });
      })
      .finally(() => {
        if (active) {
          setLoadingBase(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      console.log(BM_DEBUG_PREFIX, 'Formulario en modo alta, no se carga ensayo existente');
      setLoadingRecord(false);
      return;
    }

    let active = true;
    setLoadingRecord(true);
    const detailUrl = apiUrl(`/api/biologia-molecular/${encodeURIComponent(normalizedAssayId)}/`);
    console.log(BM_DEBUG_PREFIX, 'Solicitando detalle del ensayo molecular', {
      assayId,
      detailUrl,
      normalizedAssayId,
    });

    axios
      .get(detailUrl)
      .then((response) => {
        if (!active) {
          return;
        }

        console.log(BM_DEBUG_PREFIX, 'Detalle del ensayo molecular recibido', summarizeAssayForDebug(response.data));
        setFormData(normalizeAssayData(response.data, {}));
        setFurthestUnlockedStep(workflowStepCount);
        setCurrentStep(isClosureMode ? CLOSURE_STEP_ID : 1);
        setSelectedFile(null);
      })
      .catch((error) => {
        console.log(BM_DEBUG_PREFIX, 'Fallo al cargar detalle del ensayo molecular', summarizeAxiosErrorForDebug(error));
        console.error('Error al cargar el ensayo molecular:', error);
        if (!active) {
          return;
        }

        const message = 'No se pudo cargar el ensayo de biología molecular.';
        toast.error(message);
        setSaveNotice({ type: 'error', text: message });
      })
      .finally(() => {
        if (active) {
          setLoadingRecord(false);
        }
      });

    return () => {
      active = false;
    };
  }, [assayId, isClosureMode, isEditMode, normalizedAssayId, workflowStepCount]);

  useEffect(() => {
    if (!Array.isArray(formData.casos) || formData.casos.length === 0) {
      setAvailableSamples([]);
      setLoadingSamples(false);
      return;
    }

    let active = true;
    setLoadingSamples(true);
    const sampleQuery = formData.casos
      .map((protocol) => `protocolo_general=${encodeURIComponent(protocol)}`)
      .join('&');

    axios
      .get(apiUrl(`/api/ingresos_muestras/?${sampleQuery}`))
      .then((response) => {
        if (!active) {
          return;
        }

        const molecularSamples = (Array.isArray(response.data) ? response.data : []).filter(
          (sample) => sample.sector === 'BM'
        );
        setAvailableSamples(sortSamplesByCaseSelection(molecularSamples, formData.casos));
      })
      .catch((error) => {
        console.error('Error al cargar muestras BM de los casos:', error);
        if (!active) {
          return;
        }

        setAvailableSamples([]);
        const message = 'No se pudieron cargar las muestras de los casos para biología molecular.';
        setSaveNotice({ type: 'error', text: message });
      })
      .finally(() => {
        if (active) {
          setLoadingSamples(false);
        }
      });

    return () => {
      active = false;
    };
  }, [formData.casos]);

  const selectedSamples = useMemo(
    () => availableSamples.filter((sample) => formData.muestras.includes(sample.protocolo_muestra)),
    [availableSamples, formData.muestras]
  );

  useEffect(() => {
    if (loadingSamples) {
      return;
    }

    setFormData((prev) => {
      const nextRows = syncCorridaRowsWithPlan(
        prev.corrida_muestras,
        selectedSamples,
        prev.controles,
        prev.configuracion_placa
      );
      if (serializeCorridaRowsForComparison(prev.corrida_muestras) === serializeCorridaRowsForComparison(nextRows)) {
        return prev;
      }

      return {
        ...prev,
        corrida_muestras: nextRows,
      };
    });
  }, [loadingSamples, selectedSamples]);

  useEffect(() => {
    if (!Array.isArray(formData.corrida_muestras) || formData.corrida_muestras.length === 0) {
      if (selectedCorridaRowId) {
        setSelectedCorridaRowId('');
      }
      return;
    }

    if (selectedCorridaRowId && !formData.corrida_muestras.some((row) => row.id === selectedCorridaRowId)) {
      setSelectedCorridaRowId('');
    }
  }, [formData.corrida_muestras, selectedCorridaRowId]);

  const availableCaseOptions = useMemo(
    () => caseOptions.filter((protocol) => !formData.casos.includes(protocol)),
    [caseOptions, formData.casos]
  );

  const caseSamplesMap = useMemo(
    () =>
      formData.casos.reduce((accumulator, protocol) => {
        const available = availableSamples.filter((sample) => sample.protocolo_general === protocol);
        const selected = available.filter((sample) => formData.muestras.includes(sample.protocolo_muestra));
        const remaining = available.filter((sample) => !formData.muestras.includes(sample.protocolo_muestra));

        accumulator[protocol] = {
          available,
          selected,
          remaining,
        };
        return accumulator;
      }, {}),
    [availableSamples, formData.casos, formData.muestras]
  );

  const selectedResponsible = useMemo(
    () => users.find((user) => user.username === formData.responsable) || null,
    [formData.responsable, users]
  );

  const selectedValidator = useMemo(
    () => users.find((user) => user.username === formData.validado_por) || null,
    [formData.validado_por, users]
  );
  const assayView = useMemo(() => buildAssayViewConfig(formData.tipo_ensayo), [formData.tipo_ensayo]);
  const assaySteps = useMemo(
    () =>
      ASSAY_STEPS
        .filter((step) => step.id !== CLOSURE_STEP_ID)
        .map((step) => {
          if (step.id === 2) {
            return {
              ...step,
              description: assayView.stepDescriptions.reactivos,
            };
          }
          if (step.id === 3) {
            return {
              ...step,
              description: assayView.stepDescriptions.corrida,
            };
          }
          return step;
        }),
    [assayView]
  );

  const completedStepCount = useMemo(() => {
    let total = 0;
    for (let step = 1; step <= workflowStepCount; step += 1) {
      if (!getStepMessage(step, formData, availableSamples)) {
        total += 1;
      }
    }
    return total;
  }, [availableSamples, formData, workflowStepCount]);

  const closureStepMessage = useMemo(
    () => getStepMessage(CLOSURE_STEP_ID, formData, availableSamples),
    [availableSamples, formData]
  );

  const controlSummary = useMemo(
    () => formData.controles.filter((control) => control.estado === 'SI').length,
    [formData.controles]
  );
  const mixPlanning = useMemo(() => calculateMixPlanning(formData), [formData]);
  const matrixTissueOptionValue = useMemo(
    () => getMatrixTissueOptionValue(formData.matriz_tejido),
    [formData.matriz_tejido]
  );
  const matrixTissueOtherDetail = useMemo(
    () => getMatrixTissueOtherDetail(formData.matriz_tejido),
    [formData.matriz_tejido]
  );
  const activeControlLabels = useMemo(
    () => formData.controles.filter((control) => control.estado === 'SI').map((control) => buildControlRunLabel(control)),
    [formData.controles]
  );
  const diagnosticTargetsSummary = useMemo(
    () => buildDiagnosticTargetsSummary(formData.patogenos_blanco, formData.patogeno_blanco),
    [formData.patogeno_blanco, formData.patogenos_blanco]
  );
  const plateDiagnosticAssignments = useMemo(
    () =>
      buildPlateDiagnosticAssignments(
        formData.configuracion_placa,
        formData.patogenos_blanco,
        formData.patogeno_blanco
      ),
    [formData.configuracion_placa, formData.patogenos_blanco, formData.patogeno_blanco]
  );

  useEffect(() => {
    setFormData((prev) => {
      const currentRows = Array.isArray(prev.programa_termociclador) ? prev.programa_termociclador : [];
      const currentStages = currentRows.map((row) => row.etapa);
      const currentTemplates = [
        PCR_PROGRAM_STAGES,
        QPCR_PROGRAM_STAGES,
      ];
      const matchesTemplate = currentTemplates.some((stages) => matchesProgramRowsTemplate(currentRows, stages));
      const nextStages = getProgramStageDefaults(prev.tipo_ensayo);
      const alreadyUsingTargetTemplate = currentStages.length === nextStages.length
        && currentStages.every((stage, index) => stage === nextStages[index]);

      if (!matchesTemplate || alreadyUsingTargetTemplate) {
        return prev;
      }

      return {
        ...prev,
        programa_termociclador: createDefaultProgramRows(prev.tipo_ensayo),
      };
    });
  }, [formData.tipo_ensayo]);

  const existingAttachmentUrl = formData.archivo_adjunto ? fileUrl(formData.archivo_adjunto) : '';
  const responsibleLabel = selectedResponsible ? getUserDisplayName(selectedResponsible) : formData.responsable || 'Pendiente';
  const validatorLabel = selectedValidator ? getUserDisplayName(selectedValidator) : formData.validado_por || 'Sin validación';

  const handleTopLevelChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMixConfigChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      configuracion_mix: {
        ...prev.configuracion_mix,
        [key]: value,
      },
    }));
  };

  const handlePlateConfigChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      configuracion_placa: createDefaultPlateConfig({
        ...prev.configuracion_placa,
        [key]: value,
      }),
    }));
  };

  const handleMatrixTissueOptionChange = (value) => {
    setFormData((prev) => {
      if (!value) {
        return { ...prev, matriz_tejido: '' };
      }

      if (value === MATRIX_TISSUE_OTHER_VALUE) {
        const currentIsOther = getMatrixTissueOptionValue(prev.matriz_tejido) === MATRIX_TISSUE_OTHER_VALUE;
        return {
          ...prev,
          matriz_tejido: currentIsOther ? getMatrixTissueOtherDetail(prev.matriz_tejido) : '',
        };
      }

      return {
        ...prev,
        matriz_tejido: value,
      };
    });
  };

  const updateDiagnosticTargets = (nextTargets) => {
    const normalizedTargets = Array.isArray(nextTargets) ? nextTargets : [];
    const storedTargets = normalizedTargets.length > 0 ? normalizedTargets : [''];
    setFormData((prev) => ({
      ...prev,
      patogenos_blanco: storedTargets,
      patogeno_blanco: buildDiagnosticTargetsSummary(storedTargets),
    }));
  };

  const handleDiagnosticTargetChange = (index, value) => {
    const nextTargets = Array.isArray(formData.patogenos_blanco) ? [...formData.patogenos_blanco] : [];
    nextTargets[index] = value;
    updateDiagnosticTargets(nextTargets);
  };

  const handleAddDiagnosticTarget = () => {
    const nextTargets = Array.isArray(formData.patogenos_blanco)
      ? [...formData.patogenos_blanco, '']
      : [''];
    updateDiagnosticTargets(nextTargets);
  };

  const handleRemoveDiagnosticTarget = (index) => {
    const nextTargets = Array.isArray(formData.patogenos_blanco)
      ? formData.patogenos_blanco.filter((_, targetIndex) => targetIndex !== index)
      : [];
    updateDiagnosticTargets(nextTargets);
  };

  const buildSelectionFormState = (prev, nextCases, nextSelectedSamples) => {
    const nextSelectedProtocols = nextSelectedSamples.map((sample) => sample.protocolo_muestra);
    const firstSelectedSample = nextSelectedSamples[0];
    const firstSampleMaterial = buildSampleMaterialLabel(firstSelectedSample);
    const suggestedMatrixTissue = getCanonicalMatrixTissueOption(firstSampleMaterial) || firstSampleMaterial;

    return {
      ...prev,
      protocolo_general: nextCases[0] || '',
      casos: nextCases,
      muestras: nextSelectedProtocols,
      origen_muestras: nextSelectedProtocols.length > 0
        ? prev.origen_muestras || firstSelectedSample?.grupo_zootecnico || ''
        : prev.origen_muestras,
      matriz_tejido: nextSelectedProtocols.length > 0
        ? prev.matriz_tejido || suggestedMatrixTissue
        : prev.matriz_tejido,
      numero_muestras: nextCases.length > 0
        ? Math.max(Number(prev.numero_muestras) || 0, nextSelectedProtocols.length || 1)
        : 1,
      corrida_muestras: syncCorridaRowsWithPlan(
        prev.corrida_muestras,
        nextSelectedSamples,
        prev.controles,
        prev.configuracion_placa
      ),
    };
  };

  const handleCaseSelect = (protocol) => {
    if (!protocol || formData.casos.includes(protocol)) {
      return;
    }

    setFormData((prev) => buildSelectionFormState(prev, [...prev.casos, protocol], selectedSamples));
  };

  const handleCaseToggle = (protocol) => {
    setFormData((prev) => {
      const nextCases = prev.casos.includes(protocol)
        ? prev.casos.filter((value) => value !== protocol)
        : [...prev.casos, protocol];
      const nextSelectedSamples = availableSamples.filter(
        (sample) =>
          nextCases.includes(sample.protocolo_general) && prev.muestras.includes(sample.protocolo_muestra)
      );
      return buildSelectionFormState(prev, nextCases, nextSelectedSamples);
    });
  };

  const handleRemoveCase = (protocol) => {
    handleCaseToggle(protocol);
  };

  const handleAddSampleToCase = (caseProtocol, selectedSampleProtocol) => {
    if (!selectedSampleProtocol || formData.muestras.includes(selectedSampleProtocol)) {
      return;
    }

    const selectedSample = availableSamples.find(
      (sample) => sample.protocolo_muestra === selectedSampleProtocol
    );
    if (!selectedSample || selectedSample.protocolo_general !== caseProtocol) {
      return;
    }

    const nextSelectedProtocols = [...formData.muestras, selectedSampleProtocol];
    const nextSelectedSamples = availableSamples.filter((sample) =>
      nextSelectedProtocols.includes(sample.protocolo_muestra)
    );

    setFormData((prev) => buildSelectionFormState(prev, prev.casos, nextSelectedSamples));
  };

  const handleSampleToggle = (protocol) => {
    const nextSelectedProtocols = formData.muestras.includes(protocol)
      ? formData.muestras.filter((value) => value !== protocol)
      : [...formData.muestras, protocol];
    const nextSelectedSamples = availableSamples.filter((sample) =>
      nextSelectedProtocols.includes(sample.protocolo_muestra)
    );

    setFormData((prev) => buildSelectionFormState(prev, prev.casos, nextSelectedSamples));
  };

  const updateArrayRow = (fieldName, rowId, key, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: normalizeDynamicRows(
        fieldName,
        prev[fieldName].map((row) => {
          if (row.id !== rowId) {
            return row;
          }

          const nextRow = { ...row, [key]: value };
          if (fieldName === 'corrida_muestras' && key === 'identificacion_muestra') {
            nextRow.identificacion_muestra_editada = hasMeaningfulValue(value);
          }

          return nextRow;
        })
      ),
    }));
  };

  const addArrayRow = (fieldName, createRow) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: normalizeDynamicRows(fieldName, [...prev[fieldName], createRow()]),
    }));
  };

  const handleAddCorridaRowAtPosition = (position = '') => {
    const nextPosition = normalizePlatePosition(position, formData.configuracion_placa)
      || findNextAvailablePlatePosition(formData.corrida_muestras, formData.configuracion_placa);
    const nextRow = createCorridaRow({
      pozo_tubo: nextPosition,
    });

    setFormData((prev) => ({
      ...prev,
      corrida_muestras: normalizeDynamicRows('corrida_muestras', [...prev.corrida_muestras, nextRow]),
    }));
    setSelectedCorridaRowId(nextRow.id);
  };

  const removeArrayRow = (fieldName, rowId, fallbackFactory) => {
    setFormData((prev) => {
      const filteredRows = prev[fieldName].filter((row) => row.id !== rowId);
      return {
        ...prev,
        [fieldName]: normalizeDynamicRows(
          fieldName,
          filteredRows.length > 0 ? filteredRows : [fallbackFactory()]
        ),
      };
    });
  };

  const handleControlChange = (controlId, key, value) => {
    setFormData((prev) => {
      const nextControls = prev.controles.map((control) =>
        control.id === controlId ? { ...control, [key]: value } : control
      );

      return {
        ...prev,
        controles: nextControls,
        corrida_muestras: syncCorridaRowsWithPlan(
          prev.corrida_muestras,
          selectedSamples,
          nextControls,
          prev.configuracion_placa
        ),
      };
    });
  };

  const handleSyncCorridaPlan = () => {
    setFormData((prev) => ({
      ...prev,
      corrida_muestras: syncCorridaRowsWithPlan(
        prev.corrida_muestras,
        selectedSamples,
        prev.controles,
        prev.configuracion_placa
      ),
    }));
  };

  const handleRenumberCorridaRows = () => {
    setFormData((prev) => ({
      ...prev,
      corrida_muestras: prev.corrida_muestras.map((row, index) => ({
        ...row,
        pozo_tubo: buildPlatePositionFromIndex(index, prev.configuracion_placa) || String(index + 1),
      })),
    }));
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files?.[0] || null);
  };

  const validateStep = (step) => {
    const message = getStepMessage(step, formData, availableSamples);
    if (message) {
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return false;
    }

    setSaveNotice(null);
    return true;
  };

  const handleNextStep = () => {
    if (!validateStep(currentStep)) {
      return;
    }

    const nextStep = Math.min(currentStep + 1, workflowStepCount);
    setCurrentStep(nextStep);
    setFurthestUnlockedStep((prev) => Math.max(prev, nextStep));
  };

  const handlePreviousStep = () => {
    if (isClosureMode) {
      return;
    }
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step) => {
    if (step <= furthestUnlockedStep) {
      setCurrentStep(step);
      return;
    }

    if (step === currentStep + 1 && validateStep(currentStep)) {
      setFurthestUnlockedStep(step);
      setCurrentStep(step);
    }
  };

  const isAutoLinkedCorridaRow = (row) =>
    row.tipo_registro === 'CONTROL'
      ? Boolean(row.control_nombre)
      : Boolean(
          row.muestra_asociada
            ? formData.muestras.includes(row.muestra_asociada)
            : formData.muestras.includes(row.identificacion_muestra)
        );

  const buildPayload = () => {
    const payload = new FormData();
    const payloadMixRows = mixPlanning.computedRows;
    const payloadPrimerRows = buildLegacyPrimerRowsFromMixRows(payloadMixRows);
    const payloadDiagnosticTargets = normalizeDiagnosticTargets(formData.patogenos_blanco, formData.patogeno_blanco);
    const payloadSelectedSamples = formData.muestras.map(
      (protocol) =>
        availableSamples.find((sample) => sample.protocolo_muestra === protocol)
        || { protocolo_muestra: protocol, tipo_muestra: '', tipo_muestra_animal: '', tipo_muestra_vegetal: '' }
    );
    const payloadCorridaRows = syncCorridaRowsWithPlan(
      formData.corrida_muestras,
      payloadSelectedSamples,
      formData.controles,
      formData.configuracion_placa
    );

    if (formData.protocolo_general) {
      payload.append('protocolo_general', formData.protocolo_general);
    }
    formData.casos.forEach((protocol) => {
      payload.append('casos', protocol);
    });
    if (formData.muestras.length > 0) {
      payload.append('muestra', formData.muestras[0]);
      formData.muestras.forEach((protocol) => {
        payload.append('muestras', protocol);
      });
    }
    payload.append('tipo_ensayo', formData.tipo_ensayo);
    payload.append('responsable', formData.responsable);
    payload.append('fecha_ensayo', formData.fecha_ensayo);
    payload.append('origen_muestras', formData.origen_muestras);
    payload.append('matriz_tejido', formData.matriz_tejido);
    payload.append('numero_muestras', String(Number(formData.numero_muestras) || 1));
    payload.append('patogeno_blanco', buildDiagnosticTargetsSummary(payloadDiagnosticTargets));
    payloadDiagnosticTargets.forEach((target) => {
      payload.append('patogenos_blanco', target);
    });
    payload.append(
      'primers_sondas',
      JSON.stringify(payloadPrimerRows)
    );
    payload.append(
      'reactivos_controles',
      JSON.stringify({
        reactivos_base: formData.reactivos_base,
        configuracion_placa: createDefaultPlateConfig(formData.configuracion_placa || {}),
        configuracion_mix: formData.configuracion_mix,
        controles: formData.controles.map((control) => ({
          nombre: control.nombre,
          estado: control.estado,
          identificacion: control.identificacion,
          observaciones: control.observaciones,
        })),
      })
    );
    payload.append(
      'mix_reaccion',
      JSON.stringify(
        sanitizeRows(payloadMixRows, [
          'clave_componente',
          'reactivo',
          'identificacion_lote',
          'concentracion_inicial',
          'concentracion_final',
          'volumen_por_tubo',
          'cantidad_tubos',
          'numero_tubos_factor',
          'volumen_total',
        ])
      )
    );
    payload.append(
      'corrida_muestras',
      JSON.stringify(
        sanitizeRows(payloadCorridaRows, [
          'pozo_tubo',
          'identificacion_muestra',
          'muestra_asociada',
          'fecha_extraccion',
          'tipo_material',
          'tipo_registro',
          'control_nombre',
          'ct_resultado',
          'interpretacion',
        ])
      )
    );
    payload.append(
      'programa_termociclador',
      JSON.stringify(
        sanitizeRows(formData.programa_termociclador, [
          'etapa',
          'temperatura',
          'tiempo',
          'ciclos',
          'observaciones',
        ])
      )
    );
    payload.append('observaciones_corrida', formData.observaciones_corrida);
    payload.append('resultado_conforme', formData.resultado_conforme);
    payload.append('validado_por', formData.validado_por);
    payload.append('fecha_validacion', formData.fecha_validacion);

    if (selectedFile) {
      payload.append('archivo_adjunto', selectedFile, buildSafeUploadFileName(selectedFile.name));
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const stepsToValidate = isClosureMode
      ? [CLOSURE_STEP_ID]
      : Array.from({ length: workflowStepCount }, (_, index) => index + 1);

    for (const step of stepsToValidate) {
      const message = getStepMessage(step, formData, availableSamples);
      if (message) {
        if (!isClosureMode) {
          setCurrentStep(step);
          setFurthestUnlockedStep((prev) => Math.max(prev, step));
        }
        toast.error(message);
        setSaveNotice({ type: 'error', text: message });
        return;
      }
    }

    setSaving(true);
    setSaveNotice(null);

    try {
      console.log(BM_DEBUG_PREFIX, 'Preparando guardado de ensayo molecular', {
        formData: summarizeFormStateForDebug(formData, selectedFile),
        isEditMode,
        normalizedAssayId,
      });
      const payload = buildPayload();
      const requestUrl = isEditMode
        ? apiUrl(`/api/biologia-molecular/${encodeURIComponent(normalizedAssayId)}/`)
        : apiUrl('/api/biologia-molecular/');
      const requestMethod = isEditMode ? 'put' : 'post';
      console.log(BM_DEBUG_PREFIX, 'Payload listo para guardar ensayo molecular', {
        payload: summarizePayloadForDebug(payload),
        requestMethod,
        requestUrl,
      });

      const response = isEditMode
        ? await axios.put(requestUrl, payload, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            })
        : await axios.post(requestUrl, payload, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

      const savedRecord = response.data || {};
      console.log(BM_DEBUG_PREFIX, 'Respuesta de guardado del ensayo molecular', {
        savedRecord: summarizeAssayForDebug(savedRecord),
        status: response.status,
      });
      const successMessage = isEditMode
        ? `Ensayo ${savedRecord.ensayo_numero} actualizado correctamente.`
        : `Ensayo ${savedRecord.ensayo_numero} registrado correctamente.`;

      toast.success(successMessage);
      setSaveNotice({ type: 'success', text: successMessage });
      setSelectedFile(null);
      setFormData(normalizeAssayData(savedRecord, {}));
      setCurrentStep(isClosureMode ? CLOSURE_STEP_ID : workflowStepCount);
      setFurthestUnlockedStep(workflowStepCount);

      if (returnToCaseDetail && savedRecord.protocolo_general) {
        console.log(BM_DEBUG_PREFIX, 'Redirigiendo al caso luego del guardado', {
          protocolo_general: savedRecord.protocolo_general,
          targetPath: `/ver-caso/${savedRecord.protocolo_general}`,
        });
        navigate(`/ver-caso/${savedRecord.protocolo_general}`, {
          state: {
            bannerMessage: successMessage,
            muestraActiva: savedRecord.muestras?.[0] || savedRecord.muestra || '',
          },
        });
        return;
      }

      if (!isEditMode && savedRecord.ensayo_numero) {
        const detailPath = buildBiologiaMolecularDetailPath(savedRecord.ensayo_numero);
        console.log(BM_DEBUG_PREFIX, 'Redirigiendo al detalle del ensayo recien creado', {
          ensayo_numero: savedRecord.ensayo_numero,
          targetPath: detailPath,
        });
        navigate(detailPath, {
          replace: true,
          state: {
            protocoloGeneral: savedRecord.protocolo_general,
            protocoloMuestra: savedRecord.muestras?.[0] || savedRecord.muestra || '',
            saveMessage: successMessage,
          },
        });
      }
    } catch (error) {
      console.log(BM_DEBUG_PREFIX, 'Fallo al guardar el ensayo molecular', summarizeAxiosErrorForDebug(error));
      console.error('Error al guardar el ensayo molecular:', error.response?.data || error);
      const message = flattenErrorMessage(error.response?.data) || 'No se pudo guardar el ensayo de biología molecular.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
    } finally {
      setSaving(false);
    }
  };

  if (loadingBase || loadingRecord) {
    return (
      <div className="surface-card">
        <InlineLoader label={loadingRecord ? 'Cargando ensayo de biología molecular...' : 'Cargando sector...'} />
      </div>
    );
  }

  const associationsLoadedSection = (
    <div className="col-12">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <label className="form-label d-block mb-0">Asociaciones cargadas</label>
        {formData.casos.length > 0 ? (
          <span className="form-text my-0">Cada caso debe tener al menos una muestra BM.</span>
        ) : null}
      </div>
      {formData.casos.length === 0 ? (
        <div className="alert subtle-alert subtle-alert--info mb-0">
          Todavia no agregaste casos ni muestras al ensayo.
        </div>
      ) : loadingSamples ? (
        <InlineLoader label="Cargando muestras BM de los casos..." />
      ) : (
        <div className="molecular-chip-board">
          {formData.casos.map((protocol, index) => {
            const caseSamples = caseSamplesMap[protocol] || { available: [], selected: [], remaining: [] };
            const helperMessage =
              caseSamples.available.length === 0
                ? 'Este caso no tiene muestras BM disponibles.'
                : caseSamples.selected.length === 0
                  ? 'Selecciona una muestra para completar este caso.'
                  : caseSamples.remaining.length === 0
                    ? 'Todas las muestras disponibles ya estan asociadas.'
                    : 'Puedes sumar otra muestra desde el selector.';
            return (
              <div
                key={protocol}
                className={`molecular-chip-group molecular-chip-group--selected${
                  index === 0 ? ' molecular-chip-group--primary' : ''
                }`}
              >
                <div className="molecular-chip-group__header">
                  <div className="molecular-chip-row">
                    <span className={`molecular-association-chip molecular-association-chip--case${index === 0 ? ' molecular-association-chip--primary' : ''}`}>
                      <span>{index === 0 ? `Caso principal ${protocol}` : `Caso ${protocol}`}</span>
                      <button
                        type="button"
                        className="molecular-association-chip__remove"
                        onClick={() => handleRemoveCase(protocol)}
                        aria-label={`Quitar caso ${protocol}`}
                      >
                        <FiX />
                      </button>
                    </span>
                  </div>

                  <div className="molecular-chip-group__status">
                    <span className="molecular-selection-badge">Seleccionado</span>
                    {index === 0 ? (
                      <span className="molecular-selection-badge molecular-selection-badge--primary">
                        Caso principal
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="molecular-chip-row mt-2">
                  {caseSamples.selected.length === 0 ? (
                    <span className="molecular-association-chip molecular-association-chip--empty">
                      Sin muestra asociada
                    </span>
                  ) : (
                    caseSamples.selected.map((sample) => (
                      <span key={sample.protocolo_muestra} className="molecular-association-chip">
                        <span>{sample.protocolo_muestra}</span>
                        <button
                          type="button"
                          className="molecular-association-chip__remove"
                          onClick={() => handleSampleToggle(sample.protocolo_muestra)}
                          aria-label={`Quitar muestra ${sample.protocolo_muestra}`}
                        >
                          <FiX />
                        </button>
                      </span>
                    ))
                  )}
                </div>

                <div className="molecular-chip-group__picker">
                  <span className="molecular-chip-group__label">Agregar muestra</span>
                  <select
                    className="form-select molecular-chip-group__select"
                    value=""
                    onChange={(event) => handleAddSampleToCase(protocol, event.target.value)}
                    disabled={caseSamples.remaining.length === 0}
                  >
                    <option value="">
                      {caseSamples.remaining.length === 0
                        ? caseSamples.available.length === 0
                          ? 'Sin muestras disponibles'
                          : 'No quedan muestras por agregar'
                        : 'Selecciona una muestra...'}
                    </option>
                    {caseSamples.remaining.map((sample) => (
                      <option key={sample.protocolo_muestra} value={sample.protocolo_muestra}>
                        {sample.protocolo_muestra} - {sample.tipo_muestra || 'Sin tipo'} / {sample.grupo_zootecnico || 'Sin grupo'}
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  className={`molecular-chip-group__note${
                    caseSamples.selected.length === 0 ? ' molecular-chip-group__note--warning' : ''
                  }`}
                >
                  {helperMessage}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="surface-card">
      <div className="form-highlight mb-4">
        <div>
          <span className="section-kicker">
            {isClosureMode ? 'Cierre del ensayo' : isEditMode ? 'Biología Molecular' : 'Nuevo ensayo'}
          </span>
          <h3 className="form-title">
            {isClosureMode
              ? 'Conformidad, validación y adjuntos'
              : isEditMode
                ? 'Editar corrida PCR / RT-PCR'
                : 'Sector de biología molecular'}
          </h3>
          <p className="page-subtitle mt-2 mb-0">
            {isClosureMode
              ? 'Esta vista concentra el cierre administrativo del ensayo para dejar la validación por separado del registro técnico.'
              : 'El formulario se organiza en tres pasos para que el laboratorio complete el ensayo sin perder contexto entre la muestra, los reactivos y la corrida.'}
          </p>
        </div>
        <div className="protocol-chip">
          <FaDna />
          {formData.ensayo_numero || 'Ensayo pendiente'}
        </div>
      </div>

      {!isClosureMode ? (
        <div className="molecular-stepper mb-4">
          {assaySteps.map((step) => {
            const isActive = currentStep === step.id;
            const isComplete = step.id < currentStep && !getStepMessage(step.id, formData, availableSamples);
            const isUnlocked = step.id <= furthestUnlockedStep;

            return (
              <button
                key={step.id}
                type="button"
                className={`molecular-stepper__item${
                  isActive ? ' molecular-stepper__item--active' : ''
                }${isComplete ? ' molecular-stepper__item--complete' : ''}`}
                onClick={() => handleStepClick(step.id)}
                disabled={!isUnlocked}
              >
                <span className="molecular-stepper__index">{isComplete ? <FiCheck /> : `0${step.id}`}</span>
                <span className="molecular-stepper__copy">
                  <strong>{step.title}</strong>
                  <small>{step.description}</small>
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="result-form-grid">
        <div className="result-form-grid__main">
          {saveNotice ? (
            <div className={`alert ${saveNotice.type === 'success' ? 'alert-success' : 'subtle-alert'} mb-4`} role="status">
              {saveNotice.text}
            </div>
          ) : null}

          <form onSubmit={handleSubmit}>
            {currentStep === 1 ? (
              <div className="molecular-step-panel">
                <div className="result-summary-card mb-4">
                  <div className="final-step-card__header">
                    <span className="section-kicker">Paso 1</span>
                    <span className="final-step-badge final-step-badge--ready">Trazabilidad del ensayo</span>
                  </div>
                  <h4 className="result-summary-card__title">Datos principales</h4>
                  <p className="result-summary-card__text">
                    Vincula el ensayo a uno o varios casos, elige las muestras BM involucradas y registra el tipo de técnica, responsable y uno o varios blancos diagnósticos.
                  </p>

                  <div className="row g-3 mt-1">
                    <div className="col-12 d-none">
                      <label className="form-label d-block">Asociaciones cargadas</label>
                      {formData.casos.length === 0 ? (
                        <div className="alert subtle-alert subtle-alert--info mb-0">
                          Todavía no agregaste casos ni muestras al ensayo.
                        </div>
                      ) : (
                        <div className="molecular-chip-board">
                          {formData.casos.map((protocol, index) => {
                            const selectedCaseSamples = caseSamplesMap[protocol]?.selected || [];
                            return (
                              <div key={protocol} className="molecular-chip-group">
                                <div className="molecular-chip-row">
                                  <span className={`molecular-association-chip molecular-association-chip--case${index === 0 ? ' molecular-association-chip--primary' : ''}`}>
                                    <span>{index === 0 ? `Caso principal ${protocol}` : `Caso ${protocol}`}</span>
                                    <button
                                      type="button"
                                      className="molecular-association-chip__remove"
                                      onClick={() => handleRemoveCase(protocol)}
                                      aria-label={`Quitar caso ${protocol}`}
                                    >
                                      <FiX />
                                    </button>
                                  </span>

                                  {selectedCaseSamples.length === 0 ? (
                                    <span className="molecular-association-chip molecular-association-chip--empty">
                                      Sin muestra asociada
                                    </span>
                                  ) : (
                                    selectedCaseSamples.map((sample) => (
                                      <span key={sample.protocolo_muestra} className="molecular-association-chip">
                                        <span>{sample.protocolo_muestra}</span>
                                        <button
                                          type="button"
                                          className="molecular-association-chip__remove"
                                          onClick={() => handleSampleToggle(sample.protocolo_muestra)}
                                          aria-label={`Quitar muestra ${sample.protocolo_muestra}`}
                                        >
                                          <FiX />
                                        </button>
                                      </span>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="col-12">
                      <label className="form-label d-block">Agregar caso</label>
                      <div className="molecular-inline-picker">
                        <select
                          className="form-select"
                          value=""
                          onChange={(event) => handleCaseSelect(event.target.value)}
                          disabled={availableCaseOptions.length === 0}
                        >
                          <option value="">
                            {availableCaseOptions.length === 0 ? 'No quedan casos para agregar' : 'Selecciona un caso...'}
                          </option>
                          {availableCaseOptions.map((protocol) => (
                            <option key={protocol} value={protocol}>
                              {protocol}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-text mt-2">
                        Selecciona un caso para agregarlo. El primero agregado queda como caso principal del ensayo.
                      </div>
                    </div>

                    <div className="col-12 d-none">
                      <label className="form-label d-block">Asociaciones cargadas</label>
                      {formData.casos.length === 0 ? (
                        <div className="alert subtle-alert subtle-alert--info mb-0">
                          TodavÃ­a no agregaste casos ni muestras al ensayo.
                        </div>
                      ) : (
                        <div className="molecular-chip-board">
                          {formData.casos.map((protocol, index) => {
                            const selectedCaseSamples = caseSamplesMap[protocol]?.selected || [];
                            return (
                              <div key={protocol} className="molecular-chip-group">
                                <div className="molecular-chip-row">
                                  <span className={`molecular-association-chip molecular-association-chip--case${index === 0 ? ' molecular-association-chip--primary' : ''}`}>
                                    <span>{index === 0 ? `Caso principal ${protocol}` : `Caso ${protocol}`}</span>
                                    <button
                                      type="button"
                                      className="molecular-association-chip__remove"
                                      onClick={() => handleRemoveCase(protocol)}
                                      aria-label={`Quitar caso ${protocol}`}
                                    >
                                      <FiX />
                                    </button>
                                  </span>

                                  {selectedCaseSamples.length === 0 ? (
                                    <span className="molecular-association-chip molecular-association-chip--empty">
                                      Sin muestra asociada
                                    </span>
                                  ) : (
                                    selectedCaseSamples.map((sample) => (
                                      <span key={sample.protocolo_muestra} className="molecular-association-chip">
                                        <span>{sample.protocolo_muestra}</span>
                                        <button
                                          type="button"
                                          className="molecular-association-chip__remove"
                                          onClick={() => handleSampleToggle(sample.protocolo_muestra)}
                                          aria-label={`Quitar muestra ${sample.protocolo_muestra}`}
                                        >
                                          <FiX />
                                        </button>
                                      </span>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="col-12 d-none">
                      <label className="form-label d-block">Muestras por caso asociado</label>
                      <div className="molecular-sample-picker">
                        {formData.casos.length === 0 ? (
                          <div className="alert subtle-alert subtle-alert--info mb-0">
                            Primero agrega uno o varios casos para cargar sus muestras BM.
                          </div>
                        ) : loadingSamples ? (
                          <InlineLoader label="Cargando muestras BM de los casos..." />
                        ) : (
                          <div className="molecular-case-sample-list">
                            {formData.casos.map((protocol) => {
                              const caseSamples = caseSamplesMap[protocol] || { available: [], selected: [], remaining: [] };
                              return (
                                <article key={protocol} className="molecular-case-sample-card">
                                  <div className="molecular-case-sample-card__header">
                                    <div>
                                      <strong>{protocol}</strong>
                                      <small>
                                        {caseSamples.selected.length > 0
                                          ? `${caseSamples.selected.length} muestra(s) agregada(s)`
                                          : 'Sin muestra agregada'}
                                      </small>
                                    </div>
                                    {formData.protocolo_general === protocol ? (
                                      <span className="protocol-chip">Principal</span>
                                    ) : null}
                                  </div>

                                  {caseSamples.available.length === 0 ? (
                                    <div className="alert subtle-alert subtle-alert--info mb-0">
                                      Este caso no tiene muestras BM disponibles para asociar.
                                    </div>
                                  ) : (
                                    <>
                                      <label className="form-label d-block mb-2">Agregar muestra</label>
                                      <div className="molecular-inline-picker">
                                        <select
                                          className="form-select"
                                          value=""
                                          onChange={(event) => handleAddSampleToCase(protocol, event.target.value)}
                                          disabled={caseSamples.remaining.length === 0}
                                        >
                                          <option value="">
                                            {caseSamples.remaining.length === 0 ? 'No quedan muestras para agregar' : 'Selecciona una muestra...'}
                                          </option>
                                          {caseSamples.remaining.map((sample) => (
                                            <option key={sample.protocolo_muestra} value={sample.protocolo_muestra}>
                                              {sample.protocolo_muestra} - {sample.tipo_muestra || 'Sin tipo'} / {sample.grupo_zootecnico || 'Sin grupo'}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      {caseSamples.selected.length > 0 ? (
                                        <div className="molecular-chip-row mt-3">
                                          {caseSamples.selected.map((sample) => (
                                            <span key={sample.protocolo_muestra} className="molecular-association-chip">
                                              <span>{sample.protocolo_muestra}</span>
                                              <button
                                                type="button"
                                                className="molecular-association-chip__remove"
                                                onClick={() => handleSampleToggle(sample.protocolo_muestra)}
                                                aria-label={`Quitar muestra ${sample.protocolo_muestra}`}
                                              >
                                                <FiX />
                                              </button>
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                                          Selecciona al menos una muestra para este caso.
                                        </div>
                                      )}
                                    </>
                                  )}
                                </article>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="form-text mt-2">
                        Cada caso asociado debe tener al menos una muestra BM agregada antes de continuar.
                      </div>
                    </div>

                    {associationsLoadedSection}

                    <div className="col-12 col-lg-4">
                      <label className="form-label">Tipo de ensayo</label>
                      <select
                        className="form-select"
                        value={formData.tipo_ensayo}
                        onChange={(event) => handleTopLevelChange('tipo_ensayo', event.target.value)}
                        required
                      >
                        {TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-lg-4">
                      <label className="form-label">Responsable</label>
                      <select
                        className="form-select"
                        value={formData.responsable}
                        onChange={(event) => handleTopLevelChange('responsable', event.target.value)}
                        required
                      >
                        <option value="">Seleccione responsable...</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.username}>
                            {getUserDisplayName(user)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-lg-4">
                      <label className="form-label">Fecha del ensayo</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.fecha_ensayo}
                        onChange={(event) => handleTopLevelChange('fecha_ensayo', event.target.value)}
                        required
                      />
                    </div>

                    <div className="col-12 col-lg-4">
                      <label className="form-label">Número de muestras</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control"
                        value={formData.numero_muestras}
                        onChange={(event) => handleTopLevelChange('numero_muestras', event.target.value)}
                        required
                      />
                    </div>

                    <div className="col-12 col-lg-8">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <label className="form-label mb-0">Diagnósticos / blancos</label>
                        <button
                          type="button"
                          className="btn btn-outline-brand btn-sm"
                          onClick={handleAddDiagnosticTarget}
                        >
                          <FiPlus /> Agregar otro
                        </button>
                      </div>

                      <div className="d-flex flex-column gap-2 mt-2">
                        {(Array.isArray(formData.patogenos_blanco) && formData.patogenos_blanco.length > 0
                          ? formData.patogenos_blanco
                          : ['']).map((target, index) => (
                            <div className="input-group" key={`diagnostic-target-${index}`}>
                              <span className="input-group-text">{index + 1}</span>
                              <input
                                type="text"
                                className="form-control"
                                value={target}
                                onChange={(event) => handleDiagnosticTargetChange(index, event.target.value)}
                                placeholder={index === 0 ? 'Ej. Campylobacter fetus' : 'Ej. Tritrichomonas foetus'}
                                required={index === 0}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => handleRemoveDiagnosticTarget(index)}
                                disabled={formData.patogenos_blanco.length === 1 && !String(target || '').trim()}
                                aria-label={`Quitar diagnóstico ${index + 1}`}
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          ))}
                      </div>

                      <div className="form-text mt-2">
                        Puedes cargar uno o varios blancos dentro del mismo ensayo. Para un qPCR multiplex, agrega cada diagnóstico por separado.
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Origen de muestras</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.origen_muestras}
                        onChange={(event) => handleTopLevelChange('origen_muestras', event.target.value)}
                        placeholder="Especie, órgano, establecimiento, lote o procedencia."
                        required
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Matriz / tejido</label>
                      <select
                        className="form-select"
                        value={matrixTissueOptionValue}
                        onChange={(event) => handleMatrixTissueOptionChange(event.target.value)}
                        required
                      >
                        <option value="">Selecciona una opción...</option>
                        {MATRIX_TISSUE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                        <option value={MATRIX_TISSUE_OTHER_VALUE}>Otro</option>
                      </select>
                      <div className="form-text mt-2">
                        Si no coincide con las opciones, selecciona <strong>Otro</strong> y acláralo abajo.
                      </div>
                    </div>

                    {matrixTissueOptionValue === MATRIX_TISSUE_OTHER_VALUE ? (
                      <div className="col-12">
                        <label className="form-label">Aclarar matriz / tejido</label>
                        <input
                          type="text"
                          className="form-control"
                          value={matrixTissueOtherDetail}
                          onChange={(event) => handleTopLevelChange('matriz_tejido', event.target.value)}
                          placeholder="Ej. raspaje, hisopado conjuntival, tejido fetal"
                          required
                        />
                      </div>
                    ) : null}
                  </div>

                  {formData.casos.length > 0 || selectedSamples.length > 0 ? (
                    <div className="alert subtle-alert subtle-alert--info mt-4 mb-0">
                      Casos asociados: <strong>{formData.casos.join(', ') || 'Sin casos'}</strong>.
                      {selectedSamples.length > 0 ? (
                        <>
                          {' '}Muestras asociadas: <strong>{selectedSamples.map((sample) => sample.protocolo_muestra).join(', ')}</strong>.
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {currentStep === 2 ? (
              <div className="molecular-step-panel">
                <div className="result-summary-card molecular-card-section mb-4">
                  <div className="final-step-card__header">
                    <span className="section-kicker">Paso 2</span>
                    <span className={`final-step-badge ${mixPlanning.negativeWater ? 'final-step-badge--pending' : 'final-step-badge--ready'}`}>
                      {mixPlanning.negativeWater ? 'Revisar calculo' : assayView.prepBadge}
                    </span>
                  </div>
                  <h4 className="result-summary-card__title">{assayView.mixPlanningTitle}</h4>
                  <p className="result-summary-card__text">
                    {assayView.mixPlanningDescription}
                  </p>

                  <div className="row g-3 mt-1">
                    <div className="col-12 col-lg-4">
                      <label className="form-label">Volumen final por tubo (uL)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={formData.configuracion_mix.volumen_reaccion_objetivo}
                        onChange={(event) => handleMixConfigChange('volumen_reaccion_objetivo', event.target.value)}
                      />
                    </div>

                    <div className="col-12 col-lg-4">
                      <label className="form-label">Factor de sobrepreparacion</label>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        className="form-control"
                        value={formData.configuracion_mix.factor_sobrepreparacion}
                        onChange={(event) => handleMixConfigChange('factor_sobrepreparacion', event.target.value)}
                      />
                    </div>

                    <div className="col-12 col-lg-4">
                      <label className="form-label">Reacciones extra</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="form-control"
                        value={formData.configuracion_mix.reacciones_extra}
                        onChange={(event) => handleMixConfigChange('reacciones_extra', event.target.value)}
                      />
                    </div>
                  </div>

                  <div className="molecular-planning-grid mt-4">
                    <article className="molecular-planning-stat">
                      <span>Muestras seleccionadas</span>
                      <strong>{mixPlanning.sampleCount}</strong>
                    </article>
                    <article className="molecular-planning-stat">
                      <span>Controles en corrida</span>
                      <strong>{mixPlanning.controlCount}</strong>
                    </article>
                    <article className="molecular-planning-stat">
                      <span>Reacciones planificadas</span>
                      <strong>{formatDecimalValue(mixPlanning.totalPlannedReactions)}</strong>
                    </article>
                    <article className="molecular-planning-stat">
                      <span>Reacciones calculadas</span>
                      <strong>{formatDecimalValue(mixPlanning.totalCalculatedReactions)}</strong>
                    </article>
                  </div>

                  {activeControlLabels.length > 0 ? (
                    <div className="molecular-run-chip-list mt-3">
                      {activeControlLabels.map((label) => (
                        <span key={label} className="molecular-run-chip molecular-run-chip--control">
                          {label}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="form-text mt-3">
                      Marca los controles presentes para que tambien entren en el calculo del mix y en el plan de tubos.
                    </div>
                  )}

                  <div className={`molecular-guidance-grid molecular-guidance-grid--${assayView.key} mt-4`}>
                    {assayView.prepPoints.map((point) => (
                      <article key={point} className="molecular-guidance-item">
                        {point}
                      </article>
                    ))}
                    {assayView.includesRT ? (
                      <article className="molecular-guidance-item">
                        La tecnica seleccionada incluye una etapa de retrotranscripcion previa a la amplificacion.
                      </article>
                    ) : null}
                  </div>
                </div>

                <div className="result-summary-card molecular-card-section mb-4">
                  <h4 className="result-summary-card__title">Reactivos base y controles</h4>
                  <p className="result-summary-card__text">
                    {assayView.controlsDescription} Los que marques en &quot;Si&quot; se agregan al plan de tubos.
                  </p>

                  <div className="mb-4">
                    <label className="form-label">Reactivos base / controles</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.reactivos_base}
                      onChange={(event) => handleTopLevelChange('reactivos_base', event.target.value)}
                      placeholder="Ej. Master Mix comercial, control interno, aditivos, observaciones generales."
                    />
                  </div>

                  <div className="molecular-controls-grid">
                    {formData.controles.map((control) => (
                      <article key={control.id} className="molecular-control-card">
                        <strong>{control.nombre}</strong>
                        <label className="molecular-field">
                          <span className="form-label">Estado</span>
                          <select
                            className="form-select"
                            value={control.estado}
                            onChange={(event) => handleControlChange(control.id, 'estado', event.target.value)}
                          >
                            <option value="">Seleccionar...</option>
                            <option value="SI">Sí</option>
                            <option value="NO">No</option>
                          </select>
                        </label>
                        <label className="molecular-field">
                          <span className="form-label">Identificación</span>
                          <input
                            type="text"
                            className="form-control"
                            value={control.identificacion}
                            onChange={(event) => handleControlChange(control.id, 'identificacion', event.target.value)}
                            placeholder="Código, lote o referencia"
                          />
                        </label>
                        <label className="molecular-field">
                          <span className="form-label">Observaciones</span>
                          <textarea
                            className="form-control"
                            rows="2"
                            value={control.observaciones}
                            onChange={(event) => handleControlChange(control.id, 'observaciones', event.target.value)}
                            placeholder="Desvío, comentario o aclaración"
                          />
                        </label>
                      </article>
                    ))}
                  </div>
                </div>

                <DynamicRowsSection
                  title={assayView.mixTitle}
                  description={`${assayView.mixDescription} Los primers y la sonda se cargan directamente aquí, al inicio del mix.`}
                  rows={mixPlanning.computedRows}
                  columns={[
                    {
                      key: 'reactivo',
                      label: 'Reactivo',
                      placeholder: 'Reactivo MIX',
                      readOnly: (row) => isNucleaseFreeWaterRow(row),
                    },
                    {
                      key: 'identificacion_lote',
                      label: 'Identificación / lote',
                      placeholder: 'Código o lote',
                    },
                    {
                      key: 'concentracion_inicial',
                      label: (row) => (isForwardOrReversePrimerMixRow(row) ? 'Conc. inicial (uM)' : 'Conc. inicial'),
                      placeholder: (row) => (isForwardOrReversePrimerMixRow(row) ? 'Ej. 10 uM' : 'Ej. 10X'),
                      helperText: (row) => (isForwardOrReversePrimerMixRow(row) ? 'Para FW y RV carga la concentración inicial en micromoles.' : ''),
                      readOnly: (row) => isNucleaseFreeWaterRow(row),
                    },
                    {
                      key: 'concentracion_final',
                      label: (row) => (isForwardOrReversePrimerMixRow(row) ? 'Conc. final (uM)' : 'Conc. final'),
                      placeholder: (row) => (isForwardOrReversePrimerMixRow(row) ? 'Ej. 0.8 uM' : 'Ej. 1X'),
                      helperText: (row) => (isForwardOrReversePrimerMixRow(row) ? 'Para FW y RV carga la concentración final deseada en micromoles.' : ''),
                      readOnly: (row) => isNucleaseFreeWaterRow(row),
                    },
                    {
                      key: 'volumen_por_tubo',
                      label: 'Vol. por tubo',
                      placeholder: 'Ej. 15',
                      readOnly: (row) => isNucleaseFreeWaterRow(row),
                    },
                    {
                      key: 'cantidad_tubos',
                      label: 'Cantidad de tubos',
                      placeholder: 'Ej. 12',
                    },
                    {
                      key: 'numero_tubos_factor',
                      label: 'Tubos x factor',
                      placeholder: 'Ej. 12 x 1.1',
                      readOnly: true,
                    },
                    {
                      key: 'volumen_total',
                      label: 'Vol. total calculado',
                      placeholder: 'Ej. 198',
                      readOnly: true,
                    },
                  ]}
                  addLabel="Agregar reactivo opcional"
                  emptyState="Todavía no agregaste reactivos al mix."
                  onAdd={() => addArrayRow('mix_reaccion', createMixRow)}
                  onChange={(rowId, key, value) => updateArrayRow('mix_reaccion', rowId, key, value)}
                  onRemove={(rowId) => removeArrayRow('mix_reaccion', rowId, createMixRow)}
                  canRemoveRow={() => true}
                />

                <div className={`alert ${mixPlanning.negativeWater ? 'alert-warning' : 'subtle-alert subtle-alert--info'} mt-3 mb-0`}>
                  {mixPlanning.negativeWater ? (
                    <>
                      <FiAlertCircle /> El agua calculada queda negativa. Revisa el volumen final por tubo o los componentes cargados.
                    </>
                  ) : mixPlanning.waterIsCalculated ? (
                    <>
                      Agua libre de nucleasas calculada: <strong>{formatDecimalValue(mixPlanning.waterPerTube)} uL</strong> por tubo.
                    </>
                  ) : (
                    <>
                      Completa el volumen final por tubo y los volúmenes del mix para calcular automáticamente el agua.
                    </>
                  )}
                </div>
              </div>
            ) : null}

            {currentStep === 3 ? (
              <div className="molecular-step-panel">
                <div className="result-summary-card molecular-card-section mb-4">
                  <div className="molecular-card-section__top">
                    <div>
                      <h4 className="result-summary-card__title">{assayView.runPlanTitle}</h4>
                      <p className="result-summary-card__text">
                        {assayView.runPlanDescription}
                      </p>
                    </div>
                    <div className="molecular-inline-actions">
                      <button type="button" className="btn btn-outline-brand btn-sm" onClick={handleSyncCorridaPlan}>
                        Sincronizar tubos
                      </button>
                      <button type="button" className="btn btn-outline-brand btn-sm" onClick={handleRenumberCorridaRows}>
                        Renumerar
                      </button>
                    </div>
                  </div>

                  <div className="molecular-planning-grid">
                    <article className="molecular-planning-stat">
                      <span>{assayView.runSamplesLabel}</span>
                      <strong>{selectedSamples.length}</strong>
                    </article>
                    <article className="molecular-planning-stat">
                      <span>{assayView.runControlsLabel}</span>
                      <strong>{activeControlLabels.length}</strong>
                    </article>
                    <article className="molecular-planning-stat">
                      <span>{assayView.runTotalLabel}</span>
                      <strong>{selectedSamples.length + activeControlLabels.length}</strong>
                    </article>
                    <article className="molecular-planning-stat">
                      <span>Tipo de ensayo</span>
                      <strong>{formData.tipo_ensayo || 'PCR'}</strong>
                    </article>
                  </div>

                  {selectedSamples.length > 0 || activeControlLabels.length > 0 ? (
                    <div className="molecular-run-chip-list mt-3">
                      {selectedSamples.map((sample) => (
                        <span key={sample.protocolo_muestra} className="molecular-run-chip">
                          Muestra {sample.protocolo_muestra}
                        </span>
                      ))}
                      {activeControlLabels.map((label) => (
                        <span key={label} className="molecular-run-chip molecular-run-chip--control">
                          Control {label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className={`result-summary-card molecular-mode-card molecular-mode-card--${assayView.key} mb-4`}>
                  <div className="final-step-card__header">
                    <span className="section-kicker">{assayView.prepKicker}</span>
                    <span className="molecular-mode-chip">{assayView.familyLabel}</span>
                  </div>
                  <h4 className="result-summary-card__title">{assayView.readoutTitle}</h4>
                  <p className="result-summary-card__text">
                    {assayView.readoutDescription}
                  </p>
                  <div className="molecular-guidance-grid">
                    {assayView.readoutPoints.map((point) => (
                      <article key={point} className="molecular-guidance-item">
                        {point}
                      </article>
                    ))}
                  </div>
                </div>

                <CorridaPlateSection
                  title={assayView.runTitle}
                  description={assayView.runDescription}
                  rows={formData.corrida_muestras}
                  assayView={assayView}
                  plateConfig={formData.configuracion_placa}
                  plateDiagnosticAssignments={plateDiagnosticAssignments}
                  onPlateConfigChange={handlePlateConfigChange}
                  selectedRowId={selectedCorridaRowId}
                  onSelectRow={setSelectedCorridaRowId}
                  onAddRowAtPosition={handleAddCorridaRowAtPosition}
                  columns={[
                    { key: 'pozo_tubo', label: assayView.runPositionLabel, placeholder: assayView.runPositionPlaceholder },
                    {
                      key: 'identificacion_muestra',
                      label: 'ID muestra / protocolo',
                      placeholder: 'Ej. BM-014 o código interno',
                      readOnly: (row) => row.tipo_registro === 'CONTROL' && isAutoLinkedCorridaRow(row),
                    },
                    { key: 'fecha_extraccion', label: 'Fecha extracción', type: 'date' },
                    { key: 'tipo_material', label: 'Tipo material', placeholder: 'Ej. hisopo, suero, tejido' },
                    {
                      key: 'tipo_registro',
                      label: 'Control / muestra',
                      type: 'select',
                      readOnly: (row) => isAutoLinkedCorridaRow(row),
                      options: [
                        { value: 'MUESTRA', label: 'Muestra' },
                        { value: 'CONTROL', label: 'Control' },
                      ],
                    },
                    {
                      key: 'ct_resultado',
                      label: assayView.runResultLabel,
                      placeholder: assayView.runResultPlaceholder,
                    },
                    {
                      key: 'interpretacion',
                      label: assayView.runInterpretationLabel,
                      placeholder: assayView.runInterpretationPlaceholder,
                      wide: true,
                    },
                  ]}
                  addLabel="Agregar fila manual"
                  emptyState="Todavía no se generaron tubos para la corrida."
                  onAdd={() => addArrayRow('corrida_muestras', createCorridaRow)}
                  onChange={(rowId, key, value) => updateArrayRow('corrida_muestras', rowId, key, value)}
                  onRemove={(rowId) => removeArrayRow('corrida_muestras', rowId, createCorridaRow)}
                  canRemoveRow={(row) => !isAutoLinkedCorridaRow(row)}
                />

                <DynamicRowsSection
                  title={assayView.programTitle}
                  description={assayView.programDescription}
                  rows={formData.programa_termociclador}
                  columns={[
                    { key: 'etapa', label: 'Etapa', placeholder: 'Ej. desnaturalización inicial' },
                    { key: 'temperatura', label: 'Temperatura', placeholder: 'Ej. 95 C' },
                    { key: 'tiempo', label: 'Tiempo', placeholder: 'Ej. 30 s' },
                    { key: 'ciclos', label: 'N. ciclos', placeholder: 'Ej. 40' },
                    {
                      key: 'observaciones',
                      label: 'Observaciones',
                      type: 'textarea',
                      rows: 2,
                      wide: true,
                      placeholder: 'Ajustes, hold o notas del programa',
                    },
                  ]}
                  addLabel="Agregar etapa"
                  emptyState="Todavía no agregaste etapas del termociclador."
                  onAdd={() => addArrayRow('programa_termociclador', createProgramRow)}
                  onChange={(rowId, key, value) => updateArrayRow('programa_termociclador', rowId, key, value)}
                  onRemove={(rowId) => removeArrayRow('programa_termociclador', rowId, createProgramRow)}
                />
              </div>
            ) : null}

            {currentStep === 4 ? (
              <div className="molecular-step-panel">
                <div className="result-summary-card mb-4">
                  <div className="final-step-card__header">
                    <span className="section-kicker">Paso 4</span>
                    <span
                      className={`final-step-badge ${
                        closureStepMessage ? 'final-step-badge--pending' : 'final-step-badge--complete'
                      }`}
                    >
                      {closureStepMessage ? 'Pendiente' : 'Listo para guardar'}
                    </span>
                  </div>
                  <h4 className="result-summary-card__title">Cierre del ensayo</h4>
                  <p className="result-summary-card__text">
                    {assayView.closureDescription}
                  </p>

                  <div className="mb-4">
                    <label className="form-label">Observaciones de corrida / interpretación</label>
                    <textarea
                      className="form-control"
                      rows="5"
                      value={formData.observaciones_corrida}
                      onChange={(event) => handleTopLevelChange('observaciones_corrida', event.target.value)}
                      placeholder="Resumen interpretativo del ensayo, desvío observado, aclaraciones o decisión de laboratorio."
                    />
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-lg-4">
                      <label className="form-label">Resultado conforme</label>
                      <select
                        className="form-select"
                        value={formData.resultado_conforme}
                        onChange={(event) => handleTopLevelChange('resultado_conforme', event.target.value)}
                        required
                      >
                        <option value="">Seleccione...</option>
                        {RESULT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-lg-4">
                      <label className="form-label">Validado por</label>
                      <select
                        className="form-select"
                        value={formData.validado_por}
                        onChange={(event) => handleTopLevelChange('validado_por', event.target.value)}
                      >
                        <option value="">Sin validar todavía</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.username}>
                            {getUserDisplayName(user)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-lg-4">
                      <label className="form-label">Fecha de validación</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.fecha_validacion}
                        onChange={(event) => handleTopLevelChange('fecha_validacion', event.target.value)}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Adjuntos</label>
                      <div className="molecular-upload-field">
                        <input
                          type="file"
                          className="form-control"
                          accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,.doc,.docx"
                          onChange={handleFileChange}
                        />
                        <div className="form-text mt-2">
                          Puedes cargar gel, curva, planilla o reporte complementario de la corrida.
                        </div>
                      </div>

                      {selectedFile ? (
                        <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                          <FiUploadCloud /> Nuevo archivo seleccionado: {selectedFile.name}
                        </div>
                      ) : null}

                      {existingAttachmentUrl ? (
                        <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                          Adjunto actual:{' '}
                          <a href={existingAttachmentUrl} target="_blank" rel="noreferrer">
                            abrir archivo
                          </a>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {isClosureMode ? (
              <div className="d-flex flex-column flex-md-row justify-content-between gap-2 pt-2">
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <Link to="/biologia-molecular/dashboard" className="btn btn-outline-brand">
                    Volver al dashboard
                  </Link>
                  {formData.ensayo_numero ? (
                    <Link to={buildBiologiaMolecularDetailPath(formData.ensayo_numero)} className="btn btn-outline-brand">
                      Editar ensayo
                    </Link>
                  ) : null}
                </div>

                <div className="d-flex flex-column flex-sm-row gap-2">
                  {formData.protocolo_general ? (
                    <Link to={`/ver-caso/${formData.protocolo_general}`} className="btn btn-outline-brand">
                      Ver caso
                    </Link>
                  ) : null}

                  <button type="submit" className="btn btn-brand" disabled={saving}>
                    {saving ? 'Guardando cierre...' : 'Guardar cierre y validación'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column flex-md-row justify-content-between gap-2 pt-2">
                <button type="button" className="btn btn-outline-brand" onClick={handlePreviousStep} disabled={currentStep === 1}>
                  <FiArrowLeft /> Paso anterior
                </button>

                <div className="d-flex flex-column flex-sm-row gap-2">
                  {formData.protocolo_general ? (
                    <Link to={`/ver-caso/${formData.protocolo_general}`} className="btn btn-outline-brand">
                      Ver caso
                    </Link>
                  ) : null}

                  {currentStep < workflowStepCount ? (
                    <button type="button" className="btn btn-brand" onClick={handleNextStep}>
                      Siguiente paso <FiArrowRight />
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-brand" disabled={saving}>
                      {saving
                        ? isEditMode
                          ? 'Guardando cambios...'
                          : 'Registrando ensayo...'
                        : isEditMode
                          ? 'Guardar cambios del ensayo'
                          : 'Guardar ensayo molecular'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </form>
        </div>

        <aside className="result-form-grid__aside">
          <div className="result-summary-card mb-3">
            <span className="section-kicker">Resumen</span>
            <h4 className="result-summary-card__title">
              {isClosureMode ? 'Estado del cierre' : 'Estado del ingreso'}
            </h4>
            <p className="result-summary-card__text">
              {isClosureMode
                ? 'Completa la conformidad final, quién validó el ensayo y el soporte adjunto de la corrida.'
                : assayView.sidebarDescription}
            </p>

            <dl className="result-summary-list">
              <div>
                <dt>Ensayo</dt>
                <dd>{formData.ensayo_numero || 'Sin asignar'}</dd>
              </div>
              <div>
                <dt>{isClosureMode ? 'Vista actual' : 'Paso actual'}</dt>
                <dd>{isClosureMode ? 'Cierre y validación' : `${currentStep} de ${workflowStepCount}`}</dd>
              </div>
              <div>
                <dt>{isClosureMode ? 'Estado' : 'Pasos completos'}</dt>
                <dd>{isClosureMode ? (closureStepMessage ? 'Pendiente' : 'Listo para guardar') : `${completedStepCount} / ${workflowStepCount}`}</dd>
              </div>
              <div>
                <dt>Controles afirmativos</dt>
                <dd>{controlSummary}</dd>
              </div>
            </dl>
          </div>

          <div className="result-summary-card mb-3">
            <span className="section-kicker">Vínculos</span>
            <h4 className="result-summary-card__title">Casos y muestras</h4>
            <dl className="result-summary-list">
              <div>
                <dt>Caso principal</dt>
                <dd>{formData.protocolo_general || 'Pendiente'}</dd>
              </div>
              <div>
                <dt>Casos asociados</dt>
                <dd>{formData.casos.length > 0 ? `${formData.casos.length} seleccionado(s)` : 'Sin asociar'}</dd>
              </div>
              <div>
                <dt>Muestras BM</dt>
                <dd>{formData.muestras.length > 0 ? `${formData.muestras.length} seleccionada(s)` : 'Sin asociar'}</dd>
              </div>
              <div>
                <dt>Tipo de ensayo</dt>
                <dd>{formData.tipo_ensayo || 'Pendiente'}</dd>
              </div>
              <div>
                <dt>Blancos</dt>
                <dd>{diagnosticTargetsSummary || 'Sin cargar'}</dd>
              </div>
              <div>
                <dt>Muestras declaradas</dt>
                <dd>{formData.numero_muestras || 0}</dd>
              </div>
            </dl>

            {formData.casos.length > 0 || selectedSamples.length > 0 ? (
              <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                Casos: <strong>{formData.casos.join(', ') || 'Sin casos'}</strong>.
                {selectedSamples.length > 0 ? (
                  <>
                    {' '}Muestras: <strong>{selectedSamples.map((sample) => sample.protocolo_muestra).join(', ')}</strong>.
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="result-summary-card mb-3">
            <span className="section-kicker">Mesada</span>
            <h4 className="result-summary-card__title">Resumen del mix</h4>
            <dl className="result-summary-list">
              <div>
                <dt>Reacciones planificadas</dt>
                <dd>{formatDecimalValue(mixPlanning.totalPlannedReactions)}</dd>
              </div>
              <div>
                <dt>Reacciones calculadas</dt>
                <dd>{formatDecimalValue(mixPlanning.totalCalculatedReactions)}</dd>
              </div>
              <div>
                <dt>Volumen final</dt>
                <dd>{formData.configuracion_mix.volumen_reaccion_objetivo || 'Pendiente'} uL</dd>
              </div>
              <div>
                <dt>Agua calculada</dt>
                <dd>{mixPlanning.waterIsCalculated ? `${formatDecimalValue(mixPlanning.waterPerTube)} uL` : 'Pendiente'}</dd>
              </div>
            </dl>

            {activeControlLabels.length > 0 ? (
              <div className="molecular-run-chip-list mt-3">
                {activeControlLabels.map((label) => (
                  <span key={label} className="molecular-run-chip molecular-run-chip--control">
                    {label}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="result-summary-card">
            <span className="section-kicker">Responsables</span>
            <h4 className="result-summary-card__title">Seguimiento del ensayo</h4>
            <div className="molecular-summary-stack">
              <div className="readonly-field">
                <strong className="readonly-field__value">
                  Responsable: {responsibleLabel}
                </strong>
              </div>
              <div className="readonly-field">
                <strong className="readonly-field__value">
                  Validó: {validatorLabel}
                </strong>
              </div>
            </div>

            <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
              <FiAlertCircle /> Si completas fecha de validación, también debes elegir quién validó el ensayo.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

const getStepMessage = (step, data, availableSamples = []) => {
  const mixPlanning = calculateMixPlanning(data);
  const diagnosticTargets = normalizeDiagnosticTargets(data.patogenos_blanco, data.patogeno_blanco);
  const normalizedPlateConfig = normalizePlateConfig(data.configuracion_placa);
  const primerRows = buildLegacyPrimerRowsFromMixRows(mixPlanning.computedRows);
  const selectedSampleRows = (Array.isArray(data.muestras) ? data.muestras : []).map(
    (protocol) =>
      availableSamples.find((sample) => sample.protocolo_muestra === protocol)
      || { protocolo_muestra: protocol, tipo_muestra: '', tipo_muestra_animal: '', tipo_muestra_vegetal: '' }
  );
  const mixRows = sanitizeRows(mixPlanning.computedRows, [
    'clave_componente',
    'reactivo',
    'identificacion_lote',
    'concentracion_inicial',
    'concentracion_final',
    'volumen_por_tubo',
    'cantidad_tubos',
    'numero_tubos_factor',
    'volumen_total',
  ]);
  const corridaRows = sanitizeRows(
    syncCorridaRowsWithPlan(
      data.corrida_muestras,
      selectedSampleRows,
      data.controles,
      data.configuracion_placa
    ),
    [
    'pozo_tubo',
    'identificacion_muestra',
    'fecha_extraccion',
    'tipo_material',
    'tipo_registro',
    'control_nombre',
    'ct_resultado',
    'interpretacion',
    ]
  );
  const programRows = sanitizeRows(data.programa_termociclador, [
    'etapa',
    'temperatura',
    'tiempo',
    'ciclos',
    'observaciones',
  ]);
  const mixRowsWithDetails = data.mix_reaccion.filter((row) =>
    ['concentracion_inicial', 'concentracion_final', 'volumen_por_tubo', 'cantidad_tubos', 'numero_tubos_factor', 'volumen_total'].some(
      (key) => hasMeaningfulValue(row[key])
    )
  );
  const programRowsWithDetails = data.programa_termociclador.filter((row) =>
    ['temperatura', 'tiempo', 'ciclos', 'observaciones'].some((key) => hasMeaningfulValue(row[key]))
  );

  switch (step) {
    case 1:
      if (!Array.isArray(data.casos) || data.casos.length === 0) {
        return 'Selecciona al menos un caso para el ensayo molecular.';
      }
      if (!Array.isArray(data.muestras) || data.muestras.length === 0) {
        return 'Selecciona al menos una muestra BM para el ensayo.';
      }
      if (Array.isArray(data.casos) && data.casos.length > 0) {
        const selectedCaseProtocols = new Set(
          availableSamples
            .filter((sample) => data.muestras.includes(sample.protocolo_muestra))
            .map((sample) => sample.protocolo_general)
        );
        const hasCaseWithoutSamples = data.casos.some((protocol) => !selectedCaseProtocols.has(protocol));
        if (hasCaseWithoutSamples) {
          return 'Cada caso asociado debe tener al menos una muestra BM agregada.';
        }
      }
      if (!data.tipo_ensayo) {
        return 'Selecciona el tipo de ensayo.';
      }
      if (!data.responsable) {
        return 'Selecciona el responsable del ensayo.';
      }
      if (!data.fecha_ensayo) {
        return 'Completa la fecha del ensayo.';
      }
      if (!data.origen_muestras.trim()) {
        return 'Completa el origen de las muestras.';
      }
      if (!data.matriz_tejido.trim()) {
        return 'Completa la matriz o tejido analizado.';
      }
      if (!Number(data.numero_muestras) || Number(data.numero_muestras) < 1) {
        return 'El número de muestras debe ser mayor a cero.';
      }
      if (Number(data.numero_muestras) < data.muestras.length) {
        return 'El número de muestras no puede ser menor que la cantidad de muestras BM seleccionadas.';
      }
      if (diagnosticTargets.length === 0) {
        return 'Completa al menos un patógeno o blanco diagnóstico.';
      }
      return '';
    case 2:
      if (primerRows.length === 0) {
        return 'Completa al menos uno de los componentes de primer o sonda dentro del mix.';
      }
      if (data.controles.some((control) => !control.estado)) {
        return 'Define si cada control estuvo presente o no en la corrida.';
      }
      if (mixRows.length === 0 || mixRowsWithDetails.length === 0) {
        return 'Completa al menos una fila útil del mix de reacción con sus concentraciones o volúmenes.';
      }
      if (!mixPlanning.targetVolume) {
        return 'Completa el volumen final por tubo para calcular el mix.';
      }
      if (!mixPlanning.hasValidOverageFactor) {
        return 'El factor de sobrepreparación debe ser mayor que cero.';
      }
      if (mixPlanning.negativeWater) {
        return 'Revisa el mix: el volumen de agua libre de nucleasas queda negativo.';
      }
      return '';
    case 3:
      if (normalizedPlateConfig.plateCount > diagnosticTargets.length) {
        return `Agrega al menos ${normalizedPlateConfig.plateCount} diagnosticos/blancos para asociar una placa a cada uno.`;
      }
      if (corridaRows.length === 0) {
        return 'Agrega al menos una muestra o control a la corrida.';
      }
      if (programRows.length === 0 || programRowsWithDetails.length === 0) {
        return 'Completa al menos una etapa del termociclador con temperatura, tiempo o ciclos.';
      }
      return '';
    case 4:
      if (!data.resultado_conforme) {
        return 'Selecciona si el resultado del ensayo fue conforme, no conforme o si requiere repetición.';
      }
      if (Boolean(data.validado_por) !== Boolean(data.fecha_validacion)) {
        return 'Validado por y fecha de validación deben completarse juntos.';
      }
      return '';
    default:
      return '';
  }
};

const flattenErrorMessage = (errorData) => {
  if (!errorData) {
    return '';
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  if (Array.isArray(errorData)) {
    return errorData.map((value) => flattenErrorMessage(value)).filter(Boolean).join(' | ');
  }

  return Object.entries(errorData)
    .map(([field, value]) => {
      const detail = flattenErrorMessage(value);
      if (!detail) {
        return '';
      }
      return `${field}: ${detail}`;
    })
    .filter(Boolean)
    .join(' | ');
};

export default BiologiaMolecularForm;
